import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Plus, 
  Search,
  ArrowUpDown,
  Grid,
  List,
  Edit,
  Eye,
  Share2,
  MoreVertical,
  MapPin,
  Bed,
  Bath,
  Maximize,
  Upload,
  Download,
  Sparkles,
  X,
  Check,
  Loader,
  Network,
  Home,
  Calendar,
  User,
  Phone,
  Link as LinkIcon,
  Filter,
  Archive,
  Trash2,
  AlertTriangle,
  FileText,
  Camera
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { formatPrice, formatArea, getPropertyTypeColor, getStatusColor, copyToClipboard, formatNumberWithCommas, parseFormattedNumber, generateWhatsAppLink } from '../utils/helpers';
import { buildOfferShareText, buildOfferShareUrl, getCatalogContextFromUrl, stripTrailingMiskOfferLinks } from '../utils/offerShareUtils';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { offersService, locationService, matchingService } from '../services';
import dashboardService from '../services/dashboardService';
import catalogService from '../services/catalogService';
import { usePermissions } from '../context/PermissionsContext';
import { useNetworkStatus } from '../context/NetworkStatusContext';
import PermissionGate from '../components/PermissionGate';
import LimitBadge from '../components/LimitBadge';
import MultiSelect from '../components/MultiSelect';
import SearchableSelect from '../components/SearchableSelect';
import DropdownMenu from '../components/DropdownMenu';
import GooglePagination from '../components/GooglePagination';
import { OfferShareCard, generateAndDownloadOfferImage } from '../features/offer-share-image';
import './OffersPage.css';

// Helper to get offer display image (returns null if no image)
const getOfferImage = (offer) => {
  if (offer.coverImage) return offer.coverImage;
  if (offer.images && offer.images.length > 0) return offer.images[0];
  return null;
};

const getOfferMediaItems = (offer) => {
  const images = (offer?.images || []).map((src, index) => ({
    type: 'image',
    src,
    label: `صورة ${index + 1}`
  }));
  const videos = (offer?.videos || []).map((src, index) => ({
    type: 'video',
    src,
    label: `فيديو ${index + 1}`
  }));

  return [...images, ...videos];
};

const normalizeOwnerName = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const normalizeSaudiPhone = (phone) => {
  // Accept local and international Saudi formats, then save one clean local format.
  let normalizedPhone = String(phone || '').replace(/[\s\-\(\)]/g, '');
  if (normalizedPhone.startsWith('00966')) {
    normalizedPhone = '0' + normalizedPhone.slice(5);
  } else if (normalizedPhone.startsWith('+966')) {
    normalizedPhone = '0' + normalizedPhone.slice(4);
  } else if (normalizedPhone.startsWith('966')) {
    normalizedPhone = '0' + normalizedPhone.slice(3);
  } else if (normalizedPhone.startsWith('5')) {
    normalizedPhone = '0' + normalizedPhone;
  }
  return normalizedPhone;
};

const OffersPage = ({ creationMode = false }) => {
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'ar';
  const formatGregorianDate = useCallback((value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('ar-EG', {
      calendar: 'gregory',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);
  // eslint-disable-next-line no-unused-vars
  const { isLimitReached, getLimit } = usePermissions();
  const { networkEnabled } = useNetworkStatus();
  const { user } = useAuth();
  const isSub = user?.accountType === 'sub';
  const subPerms = user?.permissions || {};
  const [viewMode, setViewMode] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState([]);
  const [filterOfferType, setFilterOfferType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCity, setFilterCity] = useState([]);
  const [filterDistrict, setFilterDistrict] = useState([]);
  const [filterPriceMin, setFilterPriceMin] = useState('');
  const [filterPriceMax, setFilterPriceMax] = useState('');
  const [filterAreaMin, setFilterAreaMin] = useState('');
  const [filterAreaMax, setFilterAreaMax] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterDeedType, setFilterDeedType] = useState('all');
  const [filterOfferNature, setFilterOfferNature] = useState('all');
  const [filterNetwork, setFilterNetwork] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showNotionFilters, setShowNotionFilters] = useState(false);
  const [pendingFilters, setPendingFilters] = useState({
    offerType: 'all', type: [], city: [], district: [],
    priceMin: '', priceMax: '', areaMin: '', areaMax: '',
    dateFrom: '', dateTo: '', deedType: 'all', offerNature: 'all',
    status: 'all', network: ''
  });
  const [showMobileSortSheet, setShowMobileSortSheet] = useState(false);
  const [showMobileFabMenu, setShowMobileFabMenu] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [publicCatalogLink, setPublicCatalogLink] = useState('');
  const [addMethod, setAddMethod] = useState('ai');
  // 0 = choice screen, 1 = العقار, 2 = الموقع والسعر, 3 = التفاصيل, 4 = الوسائط
  const [currentStep, setCurrentStep] = useState(0);
  const [showMoreOfferFields, setShowMoreOfferFields] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [viewMediaIndex, setViewMediaIndex] = useState(0);
  const [fullscreenMedia, setFullscreenMedia] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editStep, setEditStep] = useState(1); // خطوات تعديل العرض: 1-4
  const [showUnsavedConfirmModal, setShowUnsavedConfirmModal] = useState(false);
  const [, setIsGeneratingImage] = useState(false); // Keep image generation ready while its UI is temporarily disabled.
  const shareCardRef = useRef(null);

  useEffect(() => {
    // The create form is now a full page, so only real modals lock body scrolling.
    const anyModalOpen = showViewModal || showEditModal || showDeleteModal;
    document.body.style.overflow = anyModalOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showViewModal, showEditModal, showDeleteModal]);

  const [editingOffer, setEditingOffer] = useState(null);

  useEffect(() => {
    if (showEditModal) {
      setTimeout(() => {
        document.querySelectorAll('.auto-resize-textarea, .expandable-textarea').forEach(el => {
          el.style.height = 'auto';
          el.style.height = el.scrollHeight + 'px';
        });
      }, 50);
    }
  }, [showEditModal, editingOffer]);

  const [uploadedImages, setUploadedImages] = useState([]);
  const [showMoreMenu, setShowMoreMenu] = useState(null);
  
  // eslint-disable-next-line no-unused-vars
  const [autoSaveCountdown] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const autoSaveTimerRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const autoSaveIntervalRef = useRef(null);
  
  // Network visibility modal state
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [networkOffer, setNetworkOffer] = useState(null);
  const [networkVisibleFields, setNetworkVisibleFields] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [availableNetworkFields, setAvailableNetworkFields] = useState([
    { key: 'status', label: 'حالة العرض', required: false },
    { key: 'offerNature', label: 'صفة العرض', required: false },
    // بقية الحقول العامة ظاهرة دائماً، والحقول الخاصة مخفية دائماً
  ]);

  // API state
  const [offers, setOffers] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [previousOwners, setPreviousOwners] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, total: 1, count: 0 });
  const [totalAllOffers, setTotalAllOffers] = useState(0); // إجمالي العروض بدون فلاتر
  const [notUpdatedOffers, setNotUpdatedOffers] = useState(0); // عدد العروض غير المحدثة
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // null = idle, 0-100 = uploading
  const [showMatchingPopup, setShowMatchingPopup] = useState(false);
  const [matchingPopupResults, setMatchingPopupResults] = useState([]);
  const [matchingPopupLoading, setMatchingPopupLoading] = useState(false);
  const [exportingOffers, setExportingOffers] = useState(false);
  // Offer matches state (used in view modal)
  const [showOfferMatches, setShowOfferMatches] = useState(false);
  const [offerMatches, setOfferMatches] = useState([]);
  const [offerMatchesLoading, setOfferMatchesLoading] = useState(false);
  const [offerMatchesError, setOfferMatchesError] = useState('');
  // Request preview mini-modal inside view modal
  const [previewRequest, setPreviewRequest] = useState(null);
  // Store actual file objects for upload
  const [imageFiles, setImageFiles] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [privateFiles, setPrivateFiles] = useState([]);
  const [privateFilesPreview, setPrivateFilesPreview] = useState([]);
  
  // Edit modal file states
  const [editImageFiles, setEditImageFiles] = useState([]);
  const [editVideoFiles, setEditVideoFiles] = useState([]);
  const [editUploadedImages, setEditUploadedImages] = useState([]);
  const [editUploadedVideos, setEditUploadedVideos] = useState([]);
  const [editPrivateFiles, setEditPrivateFiles] = useState([]);
  const [editDeletedPrivateImageIds, setEditDeletedPrivateImageIds] = useState([]);
  const [viewPrivateImages, setViewPrivateImages] = useState([]);
  const [loadingPrivateImages, setLoadingPrivateImages] = useState(false);
  const saveEditInFlightRef = useRef(false);
  const handleAddOfferRef = useRef(null);
  const isAddOfferDirtyRef = useRef(false);
  const handledCreatedOfferRef = useRef(null);
  // Track the short post-save retry flow so timers stop on navigation or offer changes.
  const newOfferMatchFlowRef = useRef(null);
  const offersPageMountedRef = useRef(false);

  // Location state
  const [cities, setCities] = useState([]);
  const [areasByCity, setAreasByCity] = useState({});
  const [districtsByArea, setDistrictsByArea] = useState({});
  const [allDistrictsByCity, setAllDistrictsByCity] = useState({});
  const [loadingLocations, setLoadingLocations] = useState(true);

  // ترتيب المدن: مدينة المستخدم أولاً ثم الباقي
  const sortedCities = useMemo(() => {
    if (!cities.length) return cities;
    const userCity = user?.city;
    if (!userCity) return cities;
    const match = cities.find(c => c.label === userCity);
    if (!match) return cities;
    return [match, ...cities.filter(c => c.label !== userCity)];
  }, [cities, user?.city]);

  const previousOwnerMap = useMemo(() => {
    return previousOwners.reduce((map, owner) => {
      const normalizedName = normalizeOwnerName(owner.ownerName);

      if (normalizedName) {
        map.set(normalizedName, owner);
      }

      return map;
    }, new Map());
  }, [previousOwners]);

  // Fetch cities from API (public - managed by admin)
  const fetchCities = useCallback(async () => {
    try {
      setLoadingLocations(true);
      const response = await locationService.getAllCities(currentLang);
      if (response.success && response.data.cities) {
        const priorityCities = ['الجبيل', 'الدمام'];
        const citiesData = [
          ...priorityCities.map(name => response.data.cities.find(c => (c.arLabel || c.label) === name)).filter(Boolean),
          ...response.data.cities.filter(c => !priorityCities.includes(c.arLabel || c.label)),
        ];
        setCities(citiesData);
        // Build areas and districts maps
        const areasMap = {};
        const districtsMap = {};
        const allDistrictsMap = {};
        citiesData.forEach(city => {
          areasMap[city.label] = (city.areas || []).map(a => a.label);
          const cityAllDistricts = [];
          (city.areas || []).forEach(area => {
            const areaDistricts = (area.districts || []).map(d => d.label);
            districtsMap[area.label] = areaDistricts;
            cityAllDistricts.push(...areaDistricts);
          });
          // Add unassigned districts
          (city.unassignedDistricts || []).forEach(d => cityAllDistricts.push(d.label));
          allDistrictsMap[city.label] = cityAllDistricts;
        });
        setAreasByCity(areasMap);
        setDistrictsByArea(districtsMap);
        setAllDistrictsByCity(allDistrictsMap);
      }
    } catch (err) {
      console.error('Error fetching cities:', err);
    } finally {
      setLoadingLocations(false);
    }
  }, [currentLang]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  const fetchPreviousOwners = useCallback(async () => {
    try {
      const response = await offersService.getPreviousOwners();

      if (response.success) {
        setPreviousOwners(response.data.previousOwners || []);
      }
    } catch (err) {
      console.error('Error fetching previous owners:', err);
    }
  }, []);

  useEffect(() => {
    fetchPreviousOwners();
  }, [fetchPreviousOwners]);

  // جلب عدد العروض غير المحدثة من لوحة الإحصائيات
  useEffect(() => {
    const fetchNotUpdated = async () => {
      try {
        const res = await dashboardService.getStats();
        if (res.success && res.data?.stats) {
          setNotUpdatedOffers(res.data.stats.notUpdatedOffers || 0);
        }
      } catch (e) {
        // تجاهل الخطأ — البانر لن يظهر
      }
    };
    fetchNotUpdated();
  }, []);

  const handleOwnerNameChange = useCallback((value, setOffer) => {
    const trimmedValue = String(value || '').trim();
    const matchedOwner = previousOwnerMap.get(normalizeOwnerName(value));

    setOffer((prev) => ({
      ...prev,
      ownerName: value,
      ownerPhone: !trimmedValue ? '' : matchedOwner ? (matchedOwner.ownerPhone || '') : prev.ownerPhone
    }));
  }, [previousOwnerMap]);

  // Owner dropdown state (add offer)
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
  const [ownerDropdownField, setOwnerDropdownField] = useState('');
  const [allOwners, setAllOwners] = useState([]);
  const ownerDropdownRef = useRef(null);

  const fetchAllOwners = useCallback(async () => {
    try {
      const response = await offersService.getPreviousOwners('');
      if (response.success) {
        setAllOwners(response.data.previousOwners || []);
      }
    } catch (_) {}
  }, []);

  const handleOwnerInputChange = useCallback((field, value, setOffer) => {
    if (field === 'name') {
      handleOwnerNameChange(value, setOffer);
    } else {
      // Keep spaces and + while typing so formats like +966 55 559 0799 are accepted.
      if (/^[\d\s+\-()]*$/.test(value) && value.length <= 20) {
        setOffer(prev => ({ ...prev, ownerPhone: value }));
      }
    }
    setOwnerDropdownField(field);
    setOwnerDropdownOpen(true);
  }, [handleOwnerNameChange]);

  const handleOwnerFocus = useCallback((field) => {
    setOwnerDropdownField(field);
    setOwnerDropdownOpen(true);
    if (allOwners.length === 0) fetchAllOwners();
  }, [fetchAllOwners, allOwners.length]);

  const handleSelectOwnerSuggestion = useCallback((owner) => {
    setNewOffer(prev => ({ ...prev, ownerName: owner.ownerName, ownerPhone: owner.ownerPhone || '' }));
    setOwnerDropdownOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ownerDropdownRef.current && !ownerDropdownRef.current.contains(e.target)) {
        setOwnerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const loadPublicCatalogLink = async () => {
      try {
        const response = await catalogService.getMyCatalogs({ page: 1, limit: 1 });
        const firstCatalog = response?.data?.catalogs?.[0] || response?.data?.[0];
        const catalogSlug = firstCatalog?.slug;

        if (response?.success && catalogSlug) {
          setPublicCatalogLink(`${window.location.origin}/c/${catalogSlug}`);
        } else {
          setPublicCatalogLink('');
        }
      } catch (_) {
        setPublicCatalogLink('');
      }
    };

    loadPublicCatalogLink();
  }, []);

  const getPublicOfferLink = useCallback((offer) => {
    const offerId = offer?._id || offer?.id;
    const { catalogSlug } = getCatalogContextFromUrl(publicCatalogLink);
    return buildOfferShareUrl({ username: user?.username, catalogSlug, offerId });
  }, [publicCatalogLink, user?.username]);

  const getAreasForCity = (cityLabel) => {
    return areasByCity[cityLabel] || [];
  };

  const getDistrictsForArea = (areaLabel) => {
    return districtsByArea[areaLabel] || [];
  };

  const getAllDistrictsForCity = (cityLabel) => {
    return allDistrictsByCity[cityLabel] || [];
  };

  // Fetch offers from API
  const fetchOffers = useCallback(async (forcePage = null) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: forcePage ?? pagination.current,
        limit: 20
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (filterType.length > 0) {
        params.propertyType = filterType.join(',');
      }

      if (filterOfferType !== 'all') {
        params.offerType = filterOfferType;
      }

      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }

      if (filterCity.length > 0) {
        params.city = filterCity.join(',');
      }

      if (filterDistrict.length > 0) {
        params.district = filterDistrict.join(',');
      }

      if (filterPriceMin) {
        params.priceMin = filterPriceMin;
      }

      if (filterPriceMax) {
        params.priceMax = filterPriceMax;
      }

      if (filterAreaMin) {
        params.areaMin = filterAreaMin;
      }

      if (filterAreaMax) {
        params.areaMax = filterAreaMax;
      }

      if (filterDateFrom) {
        params.dateFrom = filterDateFrom;
      }

      if (filterDateTo) {
        params.dateTo = filterDateTo;
      }

      if (filterDeedType !== 'all') {
        params.deedType = filterDeedType;
      }

      if (filterOfferNature !== 'all') {
        params.offerNature = filterOfferNature;
      }

      if (filterNetwork !== '') {
        params.showInNetwork = filterNetwork;
      }

      if (sortBy !== 'newest') {
        if (sortBy === 'oldest') {
          params.sortBy = 'createdAt';
          params.sortOrder = 'asc';
        } else if (sortBy === 'price_high') {
          params.sortBy = 'price';
          params.sortOrder = 'desc';
        } else if (sortBy === 'price_low') {
          params.sortBy = 'price';
          params.sortOrder = 'asc';
        } else if (sortBy === 'area_large') {
          params.sortBy = 'area';
          params.sortOrder = 'desc';
        } else if (sortBy === 'area_small') {
          params.sortBy = 'area';
          params.sortOrder = 'asc';
        }
      }

      const response = await offersService.getMyOffers(params);

      if (response.success) {
        setOffers(response.data.offers || []);
        const pag = response.data.pagination || { current: 1, total: 1, count: 0 };
        setPagination(pag);
        // إذا لا توجد فلاتر نشطة، نحدّث العدد الكلي
        const noFilters = !searchQuery
          && (!filterType || filterType.length === 0)
          && filterOfferType === 'all'
          && filterStatus === 'all'
          && (!filterCity || filterCity.length === 0)
          && (!filterDistrict || filterDistrict.length === 0)
          && filterOfferNature === 'all'
          && filterNetwork === '';
        if (noFilters) setTotalAllOffers(pag.count || 0);
      } else {
        setError(response.message || t('errors.fetchFailed'));
      }
    } catch (err) {
      setError(err.message || t('errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchQuery,
    filterType,
    filterOfferType,
    filterStatus,
    filterCity,
    filterDistrict,
    filterPriceMin,
    filterPriceMax,
    filterAreaMin,
    filterAreaMax,
    filterDateFrom,
    filterDateTo,
    filterDeedType,
    filterOfferNature,
    filterNetwork,
    sortBy,
    pagination.current,
    t
  ]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, current: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [
    filterType,
    filterOfferType,
    filterStatus,
    filterCity,
    filterDistrict,
    filterPriceMin,
    filterPriceMax,
    filterAreaMin,
    filterAreaMax,
    filterDateFrom,
    filterDateTo,
    filterDeedType,
    filterOfferNature,
    filterNetwork,
    sortBy
  ]);

  // Only trusted internal origins are allowed for the create-page cancel action.
  const createReturnPath = location.state?.from === '/dashboard' ? '/dashboard' : '/offers';

  // Close the standalone create page, with a safe fallback for direct links.
  const closeAddPageDirect = useCallback(() => {
    setShowUnsavedConfirmModal(false);
    setCurrentStep(0);
    setAiFailedMessage(null);
    navigate(createReturnPath);
  }, [createReturnPath, navigate]);

  const handleCloseAddPage = useCallback(() => {
    if (!isAddOfferDirtyRef.current) {
      closeAddPageDirect();
      return;
    }

    setShowUnsavedConfirmModal(true);
  }, [closeAddPageDirect]);

  const handleDiscardAddChanges = useCallback(() => {
    closeAddPageDirect();
  }, [closeAddPageDirect]);

  const handleSaveAndCloseAddModal = useCallback(async () => {
    setShowUnsavedConfirmModal(false);
    await handleAddOfferRef.current?.();
  }, []);

  const handleCancelUnsavedClose = useCallback(() => {
    setShowUnsavedConfirmModal(false);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    if (window.confirm('هل تريد إغلاق النموذج؟ سيتم فقد التعديلات غير المحفوظة.')) {
      setShowEditModal(false);
      setEditingOffer(null);
    }
  }, []);

  const togglePriceOnRequest = (checked) => {
    setNewOffer(prev => ({
      ...prev,
      priceOnRequest: checked,
      price: checked ? '' : prev.price
    }));
  };

  // FIX 3: all pre-selected defaults cleared so user must actively choose
  const [newOffer, setNewOffer] = useState({
    title: '',
    type: '',
    offerType: '',
    city: '',
    region: '',
    district: '',
    price: '',
    priceOnRequest: false,
    pricePerMeter: '',
    area: '',
    rooms: '',
    bathrooms: '',
    age: '',
    direction: '',
    street: '',
    floors: '',
    ownerStatus: '',
    deedType: '',
    acceptsPayments: '',
    offerNature: '',
    ownerName: '',
    ownerPhone: '',
    comment: '',
    electricity: '',
    water: '',
    status: 'updated',
    showInNetwork: true,
    visibleFields: ['title', 'propertyType', 'offerType', 'price', 'city', 'district', 'area', 'rooms', 'bathrooms', 'age', 'floors', 'streetWidth', 'direction', 'deedType', 'acceptsPayments', 'electricity', 'water', 'description', 'features', 'images'],
    description: '',
    features: [],
    images: [],
    videos: [],
    externalLinks: [],
    landPurpose: '',
  });

  const ownerSuggestions = useMemo(() => {
    if (!ownerDropdownField) return [];
    const nameVal = String(newOffer.ownerName || '').trim().toLowerCase();
    const phoneVal = String(newOffer.ownerPhone || '').trim();
    if (ownerDropdownField === 'name') {
      if (!nameVal) return allOwners.slice(0, 8);
      return allOwners.filter(o => normalizeOwnerName(o.ownerName).includes(nameVal));
    }
    if (ownerDropdownField === 'phone') {
      if (!phoneVal) return allOwners.slice(0, 8);
      return allOwners.filter(o => (o.ownerPhone || '').includes(phoneVal));
    }
    return [];
  }, [ownerDropdownField, newOffer.ownerName, newOffer.ownerPhone, allOwners]);

  // Districts are now loaded from API via fetchCities

  const [aiText, setAiText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [aiFailedMessage, setAiFailedMessage] = useState(null);

  useEffect(() => {
    const hasTextChanges = [
      newOffer.title,
      newOffer.type,
      newOffer.offerType,
      newOffer.district,
      newOffer.price,
      newOffer.priceOnRequest,
      newOffer.area,
      newOffer.ownerName,
      newOffer.ownerPhone,
      newOffer.comment,
      newOffer.description
    ].some((value) => String(value || '').trim().length > 0);

    isAddOfferDirtyRef.current =
      creationMode && (
        hasTextChanges ||
        (newOffer.features || []).length > 0 ||
        imageFiles.length > 0 ||
        videoFiles.length > 0 ||
        privateFiles.length > 0 ||
        String(aiText || '').trim().length > 0
      );
  }, [creationMode, newOffer, imageFiles, videoFiles, privateFiles, aiText]);

  useEffect(() => {
    if (!creationMode || !isAddOfferDirtyRef.current) return undefined;

    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [creationMode, newOffer, imageFiles, videoFiles, privateFiles, aiText]);

  const filteredOffers = offers;

  // Get unique values from current offers for filter dropdowns
  const getUniqueCitiesFromOffers = () => [...new Set(offers.filter(o => o.city).map(o => o.city))];
  const getUniqueDistrictsFromOffers = () => {
    const filtered = filterCity.length > 0 ? offers.filter(o => filterCity.includes(o.city)) : offers;
    return [...new Set(filtered.filter(o => o.district).map(o => o.district))];
  };

  // Copy public offer form link
  const handleCopyFormLink = async () => {
    const userId = user?._id || user?.id;
    const formLink = userId 
      ? `${window.location.origin}/offer-form?ref=${userId}`
      : `${window.location.origin}/offer-form`;
    await copyToClipboard(formLink);
    setCopiedLink(true);
    toast.success('تم نسخ رابط النموذج');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleAddOffer = useCallback(async () => {
    // Required fields validation
    const requiredChecks = [
      { field: 'type', value: newOffer.type },
      { field: 'offerType', value: newOffer.offerType },
      { field: 'city', value: newOffer.city },
      { field: 'district', value: newOffer.district },
      { field: 'price', value: newOffer.priceOnRequest || newOffer.price },
      { field: 'title', value: newOffer.title },
    ];

    const missingRequired = requiredChecks.filter(c => !c.value);
    if (missingRequired.length > 0) {
      toast.warning(t('offers.requiredFieldsError') || 'يرجى ملء الحقول الإلزامية');
      return;
    }

    // Fix 5: area is always required (not just for sale)
    if (!newOffer.area || Number(newOffer.area) <= 0) {
      toast.warning('يرجى إدخال المساحة');
      return;
    }
    
    // Normalize phone number if provided, including +966 55 559 0799.
    const normalizedOwnerPhone = normalizeSaudiPhone(newOffer.ownerPhone);
    
    setSaving(true);
    setUploadProgress(0);
    try {
      const offerData = {
        ...newOffer,
        price: newOffer.priceOnRequest ? 0 : Number(newOffer.price),
        priceOnRequest: Boolean(newOffer.priceOnRequest),
        area: newOffer.area ? Number(newOffer.area) : undefined,
        rooms: Number(newOffer.rooms),
        bathrooms: Number(newOffer.bathrooms),
        age: Number(newOffer.age),
        street: Number(newOffer.street),
        floors: Number(newOffer.floors),
        propertyType: newOffer.type,
        deedType: newOffer.deedType || newOffer.ownerStatus,
        acceptsPayments: newOffer.acceptsPayments,
        offerNature: newOffer.offerNature,
        ownerName: newOffer.ownerName,
        ownerPhone: normalizedOwnerPhone,
        comment: newOffer.comment,
        electricity: newOffer.electricity,
        water: newOffer.water
      };

      // Remove preview URLs from offerData (actual files will be uploaded)
      delete offerData.images;
      delete offerData.videos;

      // No cover upload: display components use the first image as the new offer's main image.
      const response = await offersService.create(offerData, imageFiles, videoFiles, null, privateFiles, (percent) => setUploadProgress(percent));
      
      if (response.success) {
        toast.success('تم إضافة العرض بنجاح!');
        
        // The standalone page hands the new id to /offers for opening and match checks.
        const returnedOfferId = response.data?.offer?._id || response.data?.offer?.id || response.data?._id || response.data?.id;
        // An absent or malformed id must never interrupt the successful save redirect.
        const newOfferId = typeof returnedOfferId === 'string' && returnedOfferId.trim() ? returnedOfferId : null;
        
        // Reset form and files
        setImageFiles([]);
        setVideoFiles([]);
        setUploadedImages([]);
        setPrivateFiles([]);
        setPrivateFilesPreview([]);
        // FIX 3: reset matches the cleared initial state
        setNewOffer({
          title: '',
          type: '',
          offerType: '',
          city: '',
          region: '',
          district: '',
          price: '',
          priceOnRequest: false,
          pricePerMeter: '',
          area: '',
          rooms: '',
          bathrooms: '',
          age: '',
          direction: '',
          street: '',
          floors: '',
          ownerStatus: '',
          deedType: '',
          acceptsPayments: '',
          offerNature: '',
          ownerName: '',
          ownerPhone: '',
          comment: '',
          electricity: '',
          water: '',
          status: 'updated',
          showInNetwork: true,
          visibleFields: ['title', 'propertyType', 'offerType', 'price', 'city', 'district', 'area', 'rooms', 'bathrooms', 'age', 'floors', 'streetWidth', 'direction', 'deedType', 'acceptsPayments', 'electricity', 'water', 'description', 'features', 'images'],
          description: '',
          features: [],
          images: [],
          videos: [],
          externalLinks: [],
          landPurpose: '',
          formLink: '',
        });

        // Return to the first offers page and request opening the new offer when an id exists.
        isAddOfferDirtyRef.current = false;
        navigate('/offers', {
          replace: true,
          state: {
            refreshFirstPage: true,
            // This state is consumed once and cleared by the offers page.
            ...(newOfferId ? { openOfferId: newOfferId, checkNewOfferMatches: true } : {})
          }
        });
      } else {
        // Check for validation errors
        if (response.errors && Array.isArray(response.errors)) {
          response.errors.forEach(err => toast.error(err.message || err.msg));
        } else if (response.error) {
          toast.error(response.error);
        } else {
          toast.error(response.message || t('errors.saveFailed'));
        }
      }
    } catch (err) {
      // Handle validation errors from backend
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        err.response.data.errors.forEach(error => toast.error(error.message || error.msg));
      } else if (err.response?.data?.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error(err.message || t('errors.saveFailed'));
      }
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  }, [newOffer, imageFiles, videoFiles, privateFiles, t, toast, navigate]);

  useEffect(() => {
    handleAddOfferRef.current = handleAddOffer;
  }, [handleAddOffer]);

  const handleDeleteOffer = useCallback(async () => {
    if (!selectedOffer) return;
    try {
      const response = await offersService.deleteOffer(selectedOffer._id || selectedOffer.id);
      if (response.success) {
        toast.success('تم نقل العرض إلى سلة المحذوفات');
        fetchOffers();
        setShowDeleteModal(false);
        setSelectedOffer(null);
      } else {
        toast.error(response.message || t('errors.deleteFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.deleteFailed'));
    }
  }, [selectedOffer, fetchOffers, t, toast]);

  const handleOpenEdit = useCallback(async (offer) => {
    const baseOffer = {
      ...offer,
      priceOnRequest: Boolean(offer.priceOnRequest) || Number(offer.price) === 0,
      externalLinks: offer.externalLinks || []
    };

    try {
      const offerId = offer._id || offer.id;
      if (offerId) {
        const privateResponse = await offersService.getPrivateImages(offerId, { includeSignedUrls: 'false' });
        if (privateResponse.success) {
          baseOffer.privateImages = privateResponse.data?.privateImages || [];
        }
      }
    } catch (_) {
      baseOffer.privateImages = [];
    }

    setEditingOffer(baseOffer);
    setEditImageFiles([]);
    setEditVideoFiles([]);
    setEditUploadedImages([]);
    setEditUploadedVideos([]);
    setEditPrivateFiles([]);
    setEditDeletedPrivateImageIds([]);
    setEditStep(1); // ابدأ من الخطوة الأولى دائماً
    setShowEditModal(true);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingOffer || saveEditInFlightRef.current) return;
    saveEditInFlightRef.current = true;
    setSaving(true);
    setUploadProgress(0);
    try {
      // Keep any legacy coverImage value in data; this form no longer uploads or clears covers.
      const dataToSend = { ...editingOffer };
      // Normalize edited owner phone before sending it to the backend.
      dataToSend.ownerPhone = normalizeSaudiPhone(editingOffer.ownerPhone);
      // Remove invalid legacy status values before sending
      if (dataToSend.status) delete dataToSend.status;
      dataToSend.priceOnRequest = Boolean(editingOffer.priceOnRequest);
      dataToSend.price = dataToSend.priceOnRequest ? 0 : Number(editingOffer.price || 0);
      // Convert all numeric fields to Number to prevent backend validation errors
      dataToSend.area = editingOffer.area ? Number(editingOffer.area) : undefined;
      dataToSend.rooms = Number(editingOffer.rooms || 0);
      dataToSend.bathrooms = Number(editingOffer.bathrooms || 0);
      dataToSend.age = Number(editingOffer.age || 0);
      dataToSend.street = Number(editingOffer.street || 0);
      dataToSend.floors = Number(editingOffer.floors || 0);
      dataToSend.kitchens = Number(editingOffer.kitchens || 0);
      // Remove status and any other fields that should not be sent to backend
      delete dataToSend.status;
      delete dataToSend.offerStatus;
      // Force valid status to avoid old enum values like 'available', 'reserved', 'sold'
      if (editDeletedPrivateImageIds.length > 0) {
        dataToSend.deletePrivateImageIds = editDeletedPrivateImageIds;
      }
      const response = await offersService.update(
        editingOffer._id || editingOffer.id,
        dataToSend,
        editImageFiles,
        editVideoFiles,
        null,
        editPrivateFiles,
        (percent) => setUploadProgress(percent)
      );
      if (response.success) {
        toast.success('تم تعديل العرض بنجاح!');
        fetchOffers();
        setShowEditModal(false);
        setEditingOffer(null);
        // Reset edit file states
        setEditImageFiles([]);
        setEditVideoFiles([]);
        setEditUploadedImages([]);
        setEditUploadedVideos([]);
        setEditPrivateFiles([]);
        setEditDeletedPrivateImageIds([]);
      } else {
        // Handle validation errors
        if (response.errors && Array.isArray(response.errors)) {
          response.errors.forEach(err => toast.error(err.message || err.msg));
        } else if (response.error) {
          toast.error(response.error);
        } else {
          toast.error(response.message || t('errors.saveFailed'));
        }
      }
    } catch (err) {
      // Handle validation errors from backend
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        err.response.data.errors.forEach(error => toast.error(error.message || error.msg));
      } else if (err.response?.data?.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error(err.message || t('errors.saveFailed'));
      }
    } finally {
      setSaving(false);
      setUploadProgress(null);
      saveEditInFlightRef.current = false;
    }
  }, [editingOffer, editImageFiles, editVideoFiles, editPrivateFiles, editDeletedPrivateImageIds, fetchOffers, t, toast]);

  const loadPrivateImagesForOffer = useCallback(async (offerId) => {
    setLoadingPrivateImages(true);
    try {
      const response = await offersService.getPrivateImages(offerId, { includeSignedUrls: 'true', expiresIn: 900 });
      if (response.success) {
        setViewPrivateImages(response.data?.privateImages || []);
      } else {
        setViewPrivateImages([]);
      }
    } catch (_) {
      setViewPrivateImages([]);
    } finally {
      setLoadingPrivateImages(false);
    }
  }, []);

  // Cancel the limited retry timer without leaving a pending promise behind.
  const cancelNewOfferMatchFlow = useCallback(() => {
    const flow = newOfferMatchFlowRef.current;
    if (!flow) return;
    flow.cancelled = true;
    if (flow.timerId) clearTimeout(flow.timerId);
    if (flow.resolveTimer) flow.resolveTimer();
    newOfferMatchFlowRef.current = null;
  }, []);

  useEffect(() => {
    offersPageMountedRef.current = true;
    // Delay the unmount check by one tick so React StrictMode's dev-only replay is ignored.
    return () => {
      offersPageMountedRef.current = false;
      setTimeout(() => {
        if (!offersPageMountedRef.current) cancelNewOfferMatchFlow();
      }, 0);
    };
  }, [cancelNewOfferMatchFlow]);

  const handleOpenView = useCallback(async (offer, options = {}) => {
    // Opening another offer manually stops the new-offer-only match flow.
    if (!options.preserveNewOfferMatchFlow) cancelNewOfferMatchFlow();
    setSelectedOffer(offer);
    setViewMediaIndex(0);
    setShowViewModal(true);
    const offerId = offer?._id || offer?.id;
    if (offerId) {
      await loadPrivateImagesForOffer(offerId);
    }
  }, [cancelNewOfferMatchFlow, loadPrivateImagesForOffer]);

  useEffect(() => {
    const offerId = location.state?.openOfferId;
    if (creationMode || !offerId || loading || handledCreatedOfferRef.current === offerId) return;

    handledCreatedOfferRef.current = offerId;
    const shouldCheckMatches = location.state?.checkNewOfferMatches === true;
    const flow = { offerId, cancelled: false, timerId: null, resolveTimer: null };
    newOfferMatchFlowRef.current = flow;

    // Consume navigation state immediately so refresh/back cannot repeat this flow.
    navigate('/offers', { replace: true, state: null });

    const waitBeforeRetry = () => new Promise((resolve) => {
      flow.resolveTimer = resolve;
      flow.timerId = setTimeout(() => {
        flow.timerId = null;
        flow.resolveTimer = null;
        resolve();
      }, 800);
    });

    const openCreatedOffer = async () => {
      try {
        // Prefer the freshly loaded first page, then fall back to a direct fetch.
        let createdOffer = offers.find((offer) => (offer._id || offer.id) === offerId);
        if (!createdOffer) {
          const response = await offersService.getById(offerId);
          createdOffer = response.success ? (response.data?.offer || response.data) : null;
        }
        if (flow.cancelled || !createdOffer) return;

        // Opening is synchronous; private-image loading must not delay match retries.
        handleOpenView(createdOffer, { preserveNewOfferMatchFlow: true });
        if (flow.cancelled || !shouldCheckMatches) return;

        setMatchingPopupLoading(true);
        let receivedValidResponse = false;

        // One immediate request plus two short retries; never continuous polling.
        for (let attempt = 0; attempt < 3 && !flow.cancelled; attempt += 1) {
          if (attempt > 0) await waitBeforeRetry();
          if (flow.cancelled) return;

          try {
            const matchesResponse = await matchingService.getMatchesForOffer(offerId, {
              page: 1, limit: 30, sortBy: 'score', sortOrder: 'desc'
            });
            if (flow.cancelled) return;
            const matches = matchesResponse?.data?.matches;
            if (!Array.isArray(matches)) continue;

            receivedValidResponse = true;
            if (matches.length > 0) {
              setOfferMatches(matches);
              setMatchingPopupResults(matches);
              setMatchingPopupLoading(false);
              setShowMatchingPopup(true);
              return;
            }
          } catch (matchError) {
            // A retry may still succeed; saving and opening the offer remain successful.
            console.error('Error loading new offer matches:', matchError);
          }
        }

        if (!flow.cancelled && receivedValidResponse) {
          toast.info('تم حفظ العرض ولم توجد مطابقات حاليًا.');
        }
      } catch (_) {
        // Saving still succeeds even when the follow-up detail fetch is unavailable.
      } finally {
        if (!flow.cancelled) setMatchingPopupLoading(false);
        if (newOfferMatchFlowRef.current === flow) newOfferMatchFlowRef.current = null;
      }
    };

    openCreatedOffer();
  }, [creationMode, handleOpenView, loading, location.state, navigate, offers, toast]);

  const handleImageUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      if (!isImage) {
        toast.warning(`الملف ${file.name} ليس صورة`);
      }
      if (!isValidSize) {
        toast.warning(`الملف ${file.name} أكبر من 5MB`);
      }
      return isImage && isValidSize;
    });
    
    if (validFiles.length === 0) return;
    
    // Store actual files for upload
    setImageFiles(prev => [...prev, ...validFiles]);
    // Create preview URLs
    const imageUrls = validFiles.map(file => URL.createObjectURL(file));
    setUploadedImages(prev => [...prev, ...imageUrls]);
    setNewOffer(prev => ({ ...prev, images: [...prev.images, ...imageUrls] }));
  }, [toast]);

  const handleVideoUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const isVideo = file.type.startsWith('video/');
      const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB
      if (!isVideo) {
        toast.warning(`الملف ${file.name} ليس فيديو`);
      }
      if (!isValidSize) {
        toast.warning(`الملف ${file.name} أكبر من 50MB`);
      }
      return isVideo && isValidSize;
    });
    
    if (validFiles.length === 0) return;
    
    // Store actual files for upload
    setVideoFiles(prev => [...prev, ...validFiles]);
    // Create preview URLs
    const videoUrls = validFiles.map(file => URL.createObjectURL(file));
    setNewOffer(prev => ({ ...prev, videos: [...prev.videos, ...videoUrls] }));
  }, [toast]);

  const validatePrivateDoc = useCallback((file) => {
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';
    const isAllowed = isImage || isPdf;
    const isValidSize = file.size <= 10 * 1024 * 1024;

    if (!isAllowed) {
      toast.warning(`الملف ${file.name} غير مدعوم. المسموح: صور أو PDF`);
      return false;
    }

    if (!isValidSize) {
      toast.warning(`الملف ${file.name} أكبر من 10MB`);
      return false;
    }

    return true;
  }, [toast]);

  const handlePrivateFilesUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles = files.filter(validatePrivateDoc);
    if (validFiles.length === 0) return;

    setPrivateFiles(prev => [...prev, ...validFiles]);
    setPrivateFilesPreview(prev => [
      ...prev,
      ...validFiles.map((file) => ({
        name: file.name,
        type: file.type,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      }))
    ]);
  }, [validatePrivateDoc]);

  const handleEditPrivateFilesUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles = files.filter(validatePrivateDoc);
    if (validFiles.length === 0) return;

    setEditPrivateFiles(prev => [...prev, ...validFiles]);
  }, [validatePrivateDoc]);

  const handleOpenPrivateDoc = useCallback(async (offerId, docOrImageId) => {
    const imageId = typeof docOrImageId === 'object' ? (docOrImageId?._id || docOrImageId?.id) : docOrImageId;
    const prefetchedSignedUrl = typeof docOrImageId === 'object' ? docOrImageId?.signedUrl : null;

    if (prefetchedSignedUrl) {
      window.open(prefetchedSignedUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (!offerId || !imageId) {
      toast.error(t('offers.privateDocuments.openFailed'));
      return;
    }

    try {
      const response = await offersService.getPrivateImageSignedUrl(offerId, imageId, 900);
      const url = response?.data?.signedUrl;
      if (response?.success && url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error(response?.message || t('offers.privateDocuments.openFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('offers.privateDocuments.openFailed'));
    }
  }, [t, toast]);

  const handleDeletePrivateDoc = useCallback(async (offerId, imageId) => {
    if (!offerId || !imageId) {
      toast.error(t('offers.privateDocuments.deleteFailed'));
      return;
    }

    // In edit modal: keep delete local until user clicks Save.
    if (editingOffer && String(editingOffer._id || editingOffer.id) === String(offerId)) {
      setEditDeletedPrivateImageIds((prev) => {
        const idStr = String(imageId);
        return prev.includes(idStr) ? prev : [...prev, idStr];
      });
      setEditingOffer(prev => ({
        ...prev,
        privateImages: (prev.privateImages || []).filter((item) => String(item._id) !== String(imageId))
      }));
      toast.success(t('offers.privateDocuments.deletedSuccess'));
      return;
    }

    try {
      const response = await offersService.deletePrivateImage(offerId, imageId);
      if (response?.success) {
        setViewPrivateImages(prev => prev.filter((item) => String(item._id) !== String(imageId)));
        if (editingOffer && String(editingOffer._id || editingOffer.id) === String(offerId)) {
          setEditingOffer(prev => ({
            ...prev,
            privateImages: (prev.privateImages || []).filter((item) => String(item._id) !== String(imageId))
          }));
        }
        toast.success(t('offers.privateDocuments.deletedSuccess'));
      } else {
        toast.error(response?.message || t('offers.privateDocuments.deleteFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('offers.privateDocuments.deleteFailed'));
    }
  }, [editingOffer, t, toast]);

  // Edit modal file handlers
  const handleEditImageUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isValidSize = file.size <= 5 * 1024 * 1024;
      if (!isImage) toast.warning(`الملف ${file.name} ليس صورة`);
      if (!isValidSize) toast.warning(`الملف ${file.name} أكبر من 5MB`);
      return isImage && isValidSize;
    });
    
    if (validFiles.length === 0) return;
    
    setEditImageFiles(prev => [...prev, ...validFiles]);
    const imageUrls = validFiles.map(file => URL.createObjectURL(file));
    setEditUploadedImages(prev => [...prev, ...imageUrls]);
  }, [toast]);

  const handleEditVideoUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const validFiles = files.filter(file => {
      const isVideo = file.type.startsWith('video/');
      const isValidSize = file.size <= 50 * 1024 * 1024;
      if (!isVideo) toast.warning(`الملف ${file.name} ليس فيديو`);
      if (!isValidSize) toast.warning(`الملف ${file.name} أكبر من 50MB`);
      return isVideo && isValidSize;
    });
    
    if (validFiles.length === 0) return;
    
    setEditVideoFiles(prev => [...prev, ...validFiles]);
    const videoUrls = validFiles.map(file => URL.createObjectURL(file));
    // Keep new previews separate so removing one also removes the matching upload file.
    setEditUploadedVideos(prev => [...prev, ...videoUrls]);
  }, [toast]);

  const handleExportOffers = useCallback(async (format) => {
    try {
      setExportingOffers(true);
      const blob = await offersService.exportMyOffers(format);
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const fileName = `offers_export_${new Date().toISOString().slice(0, 10)}.${ext}`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`تم تصدير العروض بصيغة ${ext.toUpperCase()}`);
    } catch (error) {
      toast.error(error.message || 'فشل تصدير العروض');
    } finally {
      setExportingOffers(false);
    }
  }, [toast]);

  // eslint-disable-next-line no-unused-vars
  const handleDownloadTemplate = useCallback(async () => {
    try {
      const blob = await offersService.downloadImportTemplate();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download template error:', error);
      // Fallback: generate simple CSV template
      const headers = ['العنوان', 'نوع العقار', 'نوع العرض', 'المدينة', 'الحي', 'السعر', 'المساحة', 'الغرف', 'الحمامات', 'عمر العقار', 'الواجهة', 'عرض الشارع', 'الأدوار', 'حالة الملكية', 'الوصف', 'المميزات'];
      const sampleRow = ['فيلا فاخرة في الروضة', 'فيلا', 'بيع', 'الرياض', 'الروضة', '2500000', '450', '6', '4', '5', 'شمال', '20', '2', 'حر', 'فيلا فاخرة مع حديقة ومسبح', 'مسبح، حديقة، مصعد'];
      const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
      const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    }
  }, []);

  const getRequestStatusLabel = (status) => {
    const labels = { not_updated: 'غير محدث', updated: 'محدث' };
    return labels[status] || status;
  };

  const getMissingRequestFields = (request) => {
    const missing = [];
    if (!request.clientName) missing.push('اسم العميل');
    if (!request.clientPhone) missing.push('رقم الجوال');
    if (!request.requestType) missing.push('نوع الطلب');
    if (!request.propertyType) missing.push('نوع العقار');
    if (!request.city) missing.push('المدينة');
    if (!(request.districts?.length > 0 || request.district)) missing.push('الحي');
    if (!(Number(request.budget) > 0 || request.noBudgetPreference)) missing.push('الميزانية');
    if (!(Number(request.area || request.areaMin) > 0 || request.noAreaPreference)) missing.push('المساحة');
    return missing;
  };

  // Send current offer details via WhatsApp to the matched request's client
  const handleShareOfferToRequest = (req) => {
    if (!req?.clientPhone) return;
    const offerLink = getPublicOfferLink(selectedOffer);
    const text = `السلام عليكم ${req.clientName || ''}\n\nيوجد عرض عقاري مطابق لطلبك:\n\n${selectedOffer.title || 'عرض عقاري'}\n${selectedOffer.city || ''}${selectedOffer.district ? ' - ' + selectedOffer.district : ''}\nالسعر: ${formatPrice(selectedOffer.price)} ر.س`;
    const message = buildOfferShareText({ text, url: offerLink });
    window.open(generateWhatsAppLink(req.clientPhone, message), '_blank');
  };

  // Returns color class based on match score (matches mrc-match-score CSS)
  const getScoreColor = (score) => {
    if (score >= 90) return 'score-excellent';
    if (score >= 75) return 'score-good';
    if (score >= 50) return 'score-fair';
    return 'score-low';
  };

  // Reuse one request-match interface in offer details and the post-save popup.
  const renderOfferMatchesList = ({ matches, loading: matchesLoading, error: matchesError, onPreview }) => (
    <div className="mrc-matches-list">
      {matchesLoading && <div className="mrc-matches-empty">جاري تحميل الطلبات المطابقة...</div>}
      {!matchesLoading && matchesError && <div className="mrc-matches-empty">{matchesError}</div>}
      {!matchesLoading && !matchesError && matches.length === 0 && (
        <div className="mrc-matches-empty">لا توجد طلبات مطابقة حالياً</div>
      )}
      {!matchesLoading && !matchesError && matches.map((match) => {
        const req = match.request || {};
        const reqId = req._id || req.id || match._id;
        return (
          <div key={`offer-match-${reqId}`} className="mrc-match-item">
            <span className={`mrc-match-score ${getScoreColor(match.score)}`}>{match.score || 0}%</span>
            <div className="mrc-match-main">
              <div className="mrc-match-title">{req.clientName || 'طلب عقاري'}</div>
              <div className="mrc-match-meta">
                {req.city || '-'}{req.districts?.length > 0 ? ` - ${req.districts.join('، ')}` : ''} • {formatPrice(req.budget || 0)}
              </div>
            </div>
            <div className="mrc-match-actions">
              <button
                type="button"
                className="mrc-match-view-btn"
                onClick={() => onPreview(req)}
                title="معاينة الطلب"
              >
                معاينة
              </button>
              {req.clientPhone && (
                <button
                  type="button"
                  className="mrc-match-whatsapp-btn"
                  onClick={() => handleShareOfferToRequest(req)}
                  title="مشاركة العرض عبر واتساب"
                >
                  <FaWhatsapp size={14} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  // Toggle and load matching requests for a specific offer
  const handleToggleOfferMatches = async () => {
    const willOpen = !showOfferMatches;
    setShowOfferMatches(willOpen);
    if (!willOpen || offerMatches.length > 0) return;
    setOfferMatchesLoading(true);
    setOfferMatchesError('');
    try {
      const response = await matchingService.getMatchesForOffer(selectedOffer._id, {
        page: 1, limit: 30, sortBy: 'score', sortOrder: 'desc'
      });
      if (response?.data?.matches) {
        setOfferMatches(response.data.matches);
      } else {
        setOfferMatches([]);
      }
    } catch (err) {
      setOfferMatchesError('فشل تحميل الطلبات المطابقة');
    } finally {
      setOfferMatchesLoading(false);
    }
  };

  const handleShareOffer = useCallback(async (offer) => {
    const offerLink = getPublicOfferLink(offer);
    const shareTextWithoutUrl = stripTrailingMiskOfferLinks(offer.description || offer.title);
    if (navigator.share) {
      try {
        await navigator.share({ title: offer.title, text: shareTextWithoutUrl, url: offerLink });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      navigator.clipboard.writeText(buildOfferShareText({ text: shareTextWithoutUrl, url: offerLink }));
      toast.success('تم نسخ تفاصيل العرض!');
    }
  }, [toast, getPublicOfferLink]);

  // eslint-disable-next-line no-unused-vars -- preserved for re-enabling offer-share-image later.
  const handleShareAsImage = useCallback(async (offer) => {
    if (!shareCardRef.current) return;
    setIsGeneratingImage(true);
    try {
      await generateAndDownloadOfferImage(shareCardRef.current, offer.title);
      toast.success('تم تحميل صورة العرض!');
    } catch (err) {
      toast.error('حدث خطأ أثناء إنشاء الصورة');
      console.error(err);
    } finally {
      setIsGeneratingImage(false);
    }
  }, [toast]);

  // Open network visibility modal
  const handleOpenNetworkModal = useCallback((offer) => {
    setNetworkOffer(offer);
    // Set current visible fields or defaults
    const currentFields = offer.visibleFields || ['status', 'offerNature'];
    setNetworkVisibleFields(currentFields);
    setShowNetworkModal(true);
  }, []);

  // Toggle field visibility in network
  const handleToggleNetworkField = useCallback((fieldKey) => {
    const field = availableNetworkFields.find(f => f.key === fieldKey);
    if (field?.required) return; // Can't toggle required fields
    
    setNetworkVisibleFields(prev => {
      if (prev.includes(fieldKey)) {
        return prev.filter(k => k !== fieldKey);
      } else {
        return [...prev, fieldKey];
      }
    });
  }, [availableNetworkFields]);

  // Save network visibility settings
  const handleSaveNetworkSettings = useCallback(async () => {
    if (!networkOffer) return;
    
    try {
      const response = await offersService.updateNetworkVisibility(
        networkOffer._id || networkOffer.id,
        networkVisibleFields
      );
      
      if (response.success) {
        // Update local state
        setOffers(prev => prev.map(o => 
          (o._id || o.id) === (networkOffer._id || networkOffer.id) 
            ? { ...o, showInNetwork: true, visibleFields: networkVisibleFields }
            : o
        ));
        toast.success('تم حفظ إعدادات العرض في الشبكة');
        setShowNetworkModal(false);
        setNetworkOffer(null);
      } else {
        toast.error(response.message || 'فشل حفظ الإعدادات');
      }
    } catch (err) {
      toast.error(err.message || 'فشل حفظ الإعدادات');
    }
  }, [networkOffer, networkVisibleFields, toast]);

  // Remove from network
  const handleRemoveFromNetwork = useCallback(async () => {
    if (!networkOffer) return;
    
    try {
      const response = await offersService.toggleNetwork(networkOffer._id || networkOffer.id);
      if (response.success && !response.data.showInNetwork) {
        setOffers(prev => prev.map(o => 
          (o._id || o.id) === (networkOffer._id || networkOffer.id) 
            ? { ...o, showInNetwork: false }
            : o
        ));
        toast.success('تم إزالة العرض من الشبكة');
        setShowNetworkModal(false);
        setNetworkOffer(null);
      }
    } catch (err) {
      toast.error(err.message || 'فشل إزالة العرض من الشبكة');
    }
  }, [networkOffer, toast]);

  const handleToggleNetwork = useCallback(async (offer) => {
    // If currently not in network, open modal to select fields
    if (!offer.showInNetwork) {
      handleOpenNetworkModal(offer);
    } else {
      // If in network, open modal to edit or remove
      handleOpenNetworkModal(offer);
    }
  }, [handleOpenNetworkModal]);

  const handleDuplicateOffer = useCallback(async (offer) => {
    try {
      const result = await offersService.duplicate(offer._id);
      if (result?.data?.offer) {
        setOffers(prev => [result.data.offer, ...prev]);
      }
    } catch (error) {
      console.error('Failed to duplicate offer:', error);
    }
  }, []);

  const extractExternalLinksFromText = (text) => {
    if (!text) return [];
    const urls = new Set();
    const urlRegex = /(https?:\/\/[^\s)]+)|(www\.[^\s)]+)/gi;
    const matches = text.match(urlRegex) || [];
    matches.forEach(raw => {
      const url = raw.startsWith('http') ? raw : `https://${raw}`;
      urls.add(url.replace(/[\]()<>.,;:!?]+$/g, ''));
    });
    return Array.from(urls).map(url => {
      const lower = url.toLowerCase();
      const isVideo = lower.includes('youtube.com') || lower.includes('youtu.be') || lower.includes('vimeo.com');
      return { url, type: isVideo ? 'video' : 'website', title: '' };
    });
  };

  // AI Analysis Handler - BRAND NEW VERSION
  const handleAnalyzeText = async () => {
    console.log('🎯 BUTTON CLICKED - handleAnalyzeText CALLED!');
    console.log('📝 aiText value:', aiText);
    
    if (!aiText.trim()) {
      console.log('⚠️ Empty text - showing alert');
      toast.warning(t('messages.writeDescriptionFirst'));
      return false;
    }

    // Validate that city is selected before AI analysis
    if (!newOffer.city) {
      toast.warning('⚠️ يرجى اختيار المدينة أولاً قبل استخدام الذكاء الاصطناعي');
      return false;
    }
    
    console.log('✅ Text validation passed, starting analysis...');
    setAnalyzing(true);
    console.log('🚀 Analyzing state set to TRUE');
    console.log('📡 About to call offersService.analyzeWithAI...');
    
    try {
      // Direct API call to backend with city hint
      const response = await offersService.analyzeWithAI(aiText, newOffer.city);
      console.log('📡 Backend Response:', response);
      
      if (response.success) {
        const data = response.data.extractedData;
        const isPartial = response.data.partial === true;
        console.log('✅ Extracted Data:', data, isPartial ? '(partial)' : '');
        
        // Backend now returns matched city and district from database
        // Use pre-selected city if available, otherwise use backend matched city
        const preSelectedCity = newOffer.city;
        const finalCity = preSelectedCity || data.city || '';
        const finalDistrict = data.district || '';
        
        console.log('🏙️ Pre-selected city:', preSelectedCity);
        console.log('🏙️ Backend matched city:', data.city);
        console.log('🏙️ Final city to set:', finalCity);
        console.log('🏘️ Backend matched district:', finalDistrict);
        console.log('📋 Available cities:', cities.map(c => c.label));
        console.log('📋 City exists in options:', cities.some(c => c.label === finalCity));
        
        // If city doesn't exist in options, try to find a match
        let matchedCity = finalCity;
        if (finalCity && !cities.some(c => c.label === finalCity)) {
          const foundCity = cities.find(c => 
            c.label.includes(finalCity) || finalCity.includes(c.label) ||
            c.arLabel?.includes(finalCity) || finalCity.includes(c.arLabel || '')
          );
          if (foundCity) {
            matchedCity = foundCity.label;
            console.log('🔄 Matched city from options:', matchedCity);
          }
        }
        
        // Similarly for district - search through all areas in the city
        let matchedDistrict = finalDistrict;
        let matchedRegion = '';
        if (matchedCity && finalDistrict) {
          // Find district by searching through all areas in the city
          const cityAreas = getAreasForCity(matchedCity);
          for (const area of cityAreas) {
            const areaDistricts = getDistrictsForArea(area);
            if (areaDistricts.includes(finalDistrict)) {
              matchedRegion = area;
              break;
            }
            const foundDistrict = areaDistricts.find(d => 
              d.includes(finalDistrict) || finalDistrict.includes(d)
            );
            if (foundDistrict) {
              matchedDistrict = foundDistrict;
              matchedRegion = area;
              break;
            }
          }
          console.log('📋 Matched district:', matchedDistrict, 'in area:', matchedRegion);
        }
        
        // Get property type label in Arabic
        const propertyTypeLabel = propertyTypes.find(pt => pt.value === (data.propertyType || 'villa'))?.label || '';
        
        // Get offer type label in Arabic
        const offerTypeLabel = data.offerType === 'sale' ? 'للبيع' : 
                               data.offerType === 'rent' ? 'للإيجار' : '';
        
        // Create title in format: propertyType_offerType_district_area
        // Example: فيلا للبيع الحجاز 950م
        const generatedTitle = `${propertyTypeLabel} ${offerTypeLabel} ${matchedDistrict || matchedCity} ${data.area ? data.area + 'م' : ''}`.trim();
        
        // Fill form with extracted data - leave unmentioned fields empty
        // AI should NOT fill: direction (الواجهة), offerNature (صفة العرض), private fields (ownerName, ownerPhone, comment)
        setNewOffer({
          title: generatedTitle || data.title || '',
          type: data.propertyType || '',
          offerType: data.offerType || '',
          city: matchedCity,
          region: matchedRegion,
          district: matchedDistrict,
          price: (data.priceOnRequest || !data.price || data.price === 0) ? '' : data.price.toString(),
          priceOnRequest: data.priceOnRequest === true,
          area: (data.area && data.area !== 0) ? data.area.toString() : '',
          rooms: (data.rooms && data.rooms !== 0) ? data.rooms.toString() : '',
          bathrooms: (data.bathrooms && data.bathrooms !== 0) ? data.bathrooms.toString() : '',
          age: (data.age && data.age !== 0) ? data.age.toString() : '',
          direction: '',
          street: (data.streetWidth && data.streetWidth !== 0) ? data.streetWidth.toString() : '',
          floors: (data.floors && data.floors !== 0) ? data.floors.toString() : '',
          ownerStatus: '',
          deedType: data.deedType || '',
          offerNature: '',
          ownerName: '',
          ownerPhone: '',
          comment: '',
          status: 'updated',
          showInNetwork: true,
          visibleFields: ['title', 'propertyType', 'offerType', 'price', 'city', 'district', 'area', 'rooms', 'bathrooms', 'age', 'floors', 'streetWidth', 'direction', 'deedType', 'acceptsPayments', 'electricity', 'water', 'description', 'features', 'images'],
          description: aiText,
          features: data.features || [],
          images: [],
          videos: []
        });

        if (data.rooms || data.bathrooms || data.floors || data.streetWidth || data.age) {
          setShowMoreOfferFields(true);
        }

        // Switch to form to show results
        setAddMethod('form');

        // Trigger auto-resize on description textarea after React re-renders
        setTimeout(() => {
          const textarea = document.querySelector('.auto-resize-textarea');
          if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = textarea.scrollHeight + 'px';
          }
        }, 50);

        // Show appropriate message based on what was matched
        if (isPartial) {
          setAiFailedMessage(response.data.missingFields || 'بعض الحقول ناقصة، يرجى إكمالها يدوياً');
        } else if (matchedCity && matchedDistrict) {
          toast.success('✅ تم التحليل بنجاح! تم تعبئة جميع البيانات');
        } else if (matchedCity) {
          toast.success('✅ تم التحليل! يرجى اختيار الحي يدوياً');
        } else {
          toast.success('✅ تم التحليل! يرجى اختيار المدينة والحي يدوياً');
        }
        return true;
      } else {
        console.error('❌ Failed:', response);
        // بدلاً من إيقاف المستخدم، نعرض رسالة الحقول الناقصة مع خيارَي التعديل أو الاستمرار
        const missingFields = [];
        if (response.errorCode === 'AI_ANALYSIS_FAILED') {
          missingFields.push('لم يتمكن الذكاء الاصطناعي من استخراج بعض البيانات الأساسية (مدينة، سعر، مساحة)');
        } else {
          missingFields.push(response.message || 'خطأ غير معروف');
        }
        setAiFailedMessage(missingFields.join('، '));
        return false;
      }
    } catch (error) {
      console.error('💥 Error:', error);
      setAiFailedMessage('حدث خطأ في الاتصال: ' + error.message);
      return false;
    } finally {
      setAnalyzing(false);
    }
  };

  // Simplified property types: شقة، أرض، دوبلكس، فيلا، دور، عمارة، محل، مستودع، استديو
  const propertyTypes = [
    { value: 'villa', label: t('offers.villa') },
    { value: 'apartment', label: t('offers.apartment') },
    { value: 'land', label: t('offers.land') },
    { value: 'building', label: t('offers.building') },
    { value: 'duplex', label: t('offers.duplex') },
    { value: 'farm', label: t('offers.farm') },
    { value: 'rest_house', label: t('offers.rest_house') },
    { value: 'floor', label: t('offers.floor') },
    { value: 'shop', label: t('offers.shop') },
    { value: 'warehouse', label: t('offers.warehouse') },
    { value: 'studio', label: t('offers.studio') }
  ];

  const hasActiveFilters =
    filterOfferType !== 'all' ||
    filterType.length > 0 ||
    filterStatus !== 'all' ||
    filterCity.length > 0 ||
    filterDistrict.length > 0 ||
    filterPriceMin ||
    filterPriceMax ||
    filterAreaMin ||
    filterAreaMax ||
    filterDateFrom ||
    filterDateTo ||
    filterDeedType !== 'all' ||
    filterOfferNature !== 'all' ||
    filterNetwork !== '';

  const activeFiltersCount = [
    filterOfferType !== 'all',
    filterType.length > 0,
    filterStatus !== 'all',
    filterCity.length > 0,
    filterDistrict.length > 0,
    filterPriceMin,
    filterPriceMax,
    filterAreaMin,
    filterAreaMax,
    filterDateFrom,
    filterDateTo,
    filterDeedType !== 'all',
    filterOfferNature !== 'all',
    filterNetwork !== ''
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    const cleared = {
      offerType: 'all', type: [], city: [], district: [],
      priceMin: '', priceMax: '', areaMin: '', areaMax: '',
      dateFrom: '', dateTo: '', deedType: 'all', offerNature: 'all',
      status: 'all', network: ''
    };
    setPendingFilters(cleared);
    setFilterOfferType('all');
    setFilterType([]);
    setFilterStatus('all');
    setFilterCity([]);
    setFilterDistrict([]);
    setFilterPriceMin('');
    setFilterPriceMax('');
    setFilterAreaMin('');
    setFilterAreaMax('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterDeedType('all');
    setFilterOfferNature('all');
    setFilterNetwork('');
  };

  const applyFilters = () => {
    setFilterOfferType(pendingFilters.offerType);
    setFilterType(pendingFilters.type);
    setFilterStatus(pendingFilters.status);
    setFilterCity(pendingFilters.city);
    setFilterDistrict(pendingFilters.district);
    setFilterPriceMin(pendingFilters.priceMin);
    setFilterPriceMax(pendingFilters.priceMax);
    setFilterAreaMin(pendingFilters.areaMin);
    setFilterAreaMax(pendingFilters.areaMax);
    setFilterDateFrom(pendingFilters.dateFrom);
    setFilterDateTo(pendingFilters.dateTo);
    setFilterDeedType(pendingFilters.deedType);
    setFilterOfferNature(pendingFilters.offerNature);
    setFilterNetwork(pendingFilters.network);
    setPagination(prev => ({ ...prev, current: 1 }));
    setShowNotionFilters(false);
  };

  return (
    <PermissionGate feature="PAGE_OFFERS" type="page">
    <div className={`offers-page${creationMode ? ' new-offer-page' : ''}`}>
      {!creationMode && (
      <>
      <div className="page-header">
        <div className="header-title">
          <Building2 size={28} />
          <h1>{t('offers.myOffers')}</h1>
          {/* العدد الكلي للعروض */}
          {totalAllOffers > 0 && (
            <span className="offers-total-badge">{totalAllOffers} عرض</span>
          )}
          {/* العدد الظاهر بعد الفلترة — يظهر فقط عند وجود فلاتر نشطة */}
          {pagination.count !== totalAllOffers && totalAllOffers > 0 && (
            <span className="offers-filtered-badge">{pagination.count} نتيجة</span>
          )}
          {/* شارة حد الاشتراك */}
          <LimitBadge limitCode="LIMIT_OFFERS" currentCount={totalAllOffers} />
        </div>
        <div className="header-actions desktop-header-actions">
          <button className="btn btn-outline" onClick={handleCopyFormLink}>
            <LinkIcon size={18} />
            {copiedLink ? t('common.copied') : 'نسخ رابط النموذج'}
          </button>
          {!(isSub && subPerms.addListings === false) && (
          <PermissionGate
            feature="LIMIT_OFFERS"
            type="limit"
            currentCount={offers.length}
            hideWhenDenied={false}
            fallback={
              <button className="btn btn-disabled" disabled title={t('permissions.limitReached')}>
                <Plus size={20} />
                {t('offers.addOffer')}
              </button>
            }
          >
            <button className="btn btn-primary" onClick={() => navigate('/offers/new', { state: { from: '/offers' } })}>
              <Plus size={20} />
              {t('offers.addOffer')}
            </button>
          </PermissionGate>
          )}
        </div>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <Search size={20} />
          <input 
            type="text"
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="filters notion-desktop-filters" style={{ alignItems: 'center' }}>
          <button 
            className={`notion-filter-btn ${showNotionFilters ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
            onClick={() => {
              if (!showNotionFilters) {
                setPendingFilters({
                  offerType: filterOfferType, type: filterType, city: filterCity,
                  district: filterDistrict, priceMin: filterPriceMin, priceMax: filterPriceMax,
                  areaMin: filterAreaMin, areaMax: filterAreaMax, dateFrom: filterDateFrom,
                  dateTo: filterDateTo, deedType: filterDeedType, offerNature: filterOfferNature,
                  status: filterStatus, network: filterNetwork
                });
              }
              setShowNotionFilters(!showNotionFilters);
              setShowMobileSortSheet(false);
            }}
          >
            <Filter size={18} />
            {activeFiltersCount > 0 ? <span className="filter-count-badge">{activeFiltersCount}</span> : null}
          </button>
          {hasActiveFilters && (
            <button className="clear-all-inline" onClick={clearAllFilters}>✕</button>
          )}

          <div className="filter-group">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="newest">الأحدث أولاً</option>
              <option value="oldest">الأقدم أولاً</option>
              <option value="price_high">السعر (الأعلى أولاً)</option>
              <option value="price_low">السعر (الأقل أولاً)</option>
              <option value="area_large">المساحة (الأكبر أولاً)</option>
              <option value="area_small">المساحة (الأصغر أولاً)</option>
            </select>
          </div>
        </div>

        <div className="view-toggle notion-desktop-view-toggle">
          <button
            className={viewMode === 'grid' ? 'active' : ''}
            onClick={() => setViewMode('grid')}
            title="شبكي"
          >
            <Grid size={20} />
          </button>
          <button
            className={viewMode === 'list' ? 'active' : ''}
            onClick={() => setViewMode('list')}
            title="قائمة"
          >
            <List size={20} />
          </button>
        </div>

        {/* أزرار التصدير — ديسكتوب */}
        <div className="notion-desktop-filters" style={{ gap: '6px' }}>
          <button className="btn btn-outline btn-sm" onClick={() => handleExportOffers('xlsx')} disabled={exportingOffers} title="تصدير Excel">
            <Download size={16} />
            <span>Excel</span>
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => handleExportOffers('pdf')} disabled={exportingOffers} title="تصدير PDF">
            <Download size={16} />
            <span>PDF</span>
          </button>
        </div>

        <div className="mobile-actions-row" aria-label="أدوات العرض">
          <button
            className={`mobile-icon-btn ${showNotionFilters ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
            onClick={() => {
              if (!showNotionFilters) {
                setPendingFilters({
                  offerType: filterOfferType, type: filterType, city: filterCity,
                  district: filterDistrict, priceMin: filterPriceMin, priceMax: filterPriceMax,
                  areaMin: filterAreaMin, areaMax: filterAreaMax, dateFrom: filterDateFrom,
                  dateTo: filterDateTo, deedType: filterDeedType, offerNature: filterOfferNature,
                  status: filterStatus, network: filterNetwork
                });
              }
              setShowNotionFilters(!showNotionFilters);
              setShowMobileSortSheet(false);
            }}
            title="الفلاتر"
          >
            <Filter size={17} />
            {activeFiltersCount > 0 ? <span className="filter-count-badge">{activeFiltersCount}</span> : null}
          </button>

          <button
            className={`mobile-icon-btn ${showMobileSortSheet ? 'active' : ''}`}
            onClick={() => {
              setShowMobileSortSheet(!showMobileSortSheet);
              setShowNotionFilters(false);
            }}
            title="الفرز"
          >
            <ArrowUpDown size={17} />
          </button>

          <button
            className="mobile-icon-btn"
            onClick={handleCopyFormLink}
            title={copiedLink ? t('common.copied') : 'نسخ رابط الفورم'}
          >
            <LinkIcon size={16} />
          </button>

          {/* زر تصدير — جوال */}
          <button
            className="mobile-icon-btn"
            onClick={() => handleExportOffers('xlsx')}
            disabled={exportingOffers}
            title="تصدير Excel"
          >
            <Download size={16} />
          </button>

          <div className="mobile-view-toggle" role="group" aria-label="طريقة العرض">
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
              title="عرض شبكي"
            >
              <Grid size={16} />
            </button>
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              title="عرض قائمة"
            >
              <List size={16} />
            </button>
          </div>
        </div>

        {/* زر إضافة عرض للموبايل */}
        {!(isSub && subPerms.addListings === false) && (
        <PermissionGate
          feature="LIMIT_OFFERS"
          type="limit"
          currentCount={offers.length}
          hideWhenDenied={false}
          fallback={
            <button className="btn btn-disabled mobile-add-offer-btn" disabled>
              <Plus size={18} />
              {t('offers.addOffer')}
            </button>
          }
        >
          <button className="btn btn-primary mobile-add-offer-btn" data-tour="add-offer-btn" onClick={() => navigate('/offers/new', { state: { from: '/offers' } })}>
            <Plus size={18} />
            {t('offers.addOffer')}
          </button>
        </PermissionGate>
        )}

      </div>

      {/* Notion-style Filters Panel */}
      {showNotionFilters && (
        <div className="notion-sheet-backdrop" onClick={() => setShowNotionFilters(false)}>
          <div className="notion-filters-panel" onClick={(e) => e.stopPropagation()}>
            <div className="notion-filters-header">
              <span>الفلاتر</span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {hasActiveFilters && (
                  <button className="notion-clear-btn" onClick={clearAllFilters}>مسح الكل</button>
                )}
                <button className="btn btn-primary btn-sm" onClick={applyFilters}>تطبيق</button>
              </div>
            </div>
            <div className="notion-filters-body">
            <div className="notion-filter-row">
              <label>{t('offers.offerType')}</label>
              <select value={pendingFilters.offerType} onChange={(e) => setPendingFilters(p => ({ ...p, offerType: e.target.value }))}>
                <option value="all">{t('common.all')}</option>
                <option value="sale">{t('offers.forSale')}</option>
                <option value="rent">{t('offers.forRent')}</option>
              </select>
            </div>
            <div className="notion-filter-row">
              <label>{t('offers.propertyType')}</label>
              <MultiSelect
                options={propertyTypes.map(type => ({ value: type.value, label: type.label }))}
                value={pendingFilters.type}
                onChange={(val) => setPendingFilters(p => ({ ...p, type: val }))}
                placeholder={t('common.all')}
                searchPlaceholder="ابحث عن نوع..."
              />
            </div>
            <div className="notion-filter-row">
              <label>{t('offers.city')}</label>
              <MultiSelect
                options={getUniqueCitiesFromOffers().map(city => ({ value: city, label: city }))}
                value={pendingFilters.city}
                onChange={(val) => setPendingFilters(p => ({ ...p, city: val, district: [] }))}
                placeholder={t('common.all')}
                searchPlaceholder="ابحث عن مدينة..."
              />
            </div>
            <div className="notion-filter-row">
              <label>{t('offers.district')}</label>
              <MultiSelect
                options={getUniqueDistrictsFromOffers().map(d => ({ value: d, label: d }))}
                value={pendingFilters.district}
                onChange={(val) => setPendingFilters(p => ({ ...p, district: val }))}
                placeholder={t('common.all')}
                searchPlaceholder="ابحث عن حي..."
              />
            </div>
            <div className="notion-filter-row">
              <label>السعر</label>
              <div className="notion-range-inputs">
                <input type="text" placeholder="من" value={formatNumberWithCommas(pendingFilters.priceMin)} onChange={(e) => setPendingFilters(p => ({ ...p, priceMin: parseFormattedNumber(e.target.value) }))} />
                <span>-</span>
                <input type="text" placeholder="إلى" value={formatNumberWithCommas(pendingFilters.priceMax)} onChange={(e) => setPendingFilters(p => ({ ...p, priceMax: parseFormattedNumber(e.target.value) }))} />
              </div>
            </div>
            <div className="notion-filter-row">
              <label>المساحة (م²)</label>
              <div className="notion-range-inputs">
                <input type="text" placeholder="من" value={formatNumberWithCommas(pendingFilters.areaMin)} onChange={(e) => setPendingFilters(p => ({ ...p, areaMin: parseFormattedNumber(e.target.value) }))} />
                <span>-</span>
                <input type="text" placeholder="إلى" value={formatNumberWithCommas(pendingFilters.areaMax)} onChange={(e) => setPendingFilters(p => ({ ...p, areaMax: parseFormattedNumber(e.target.value) }))} />
              </div>
            </div>
            <div className="notion-filter-row">
              <label>تاريخ الإضافة</label>
              <div className="notion-date-range-inputs">
                <label className="notion-date-field-label">
                  <span>من</span>
                  <input type="date" value={pendingFilters.dateFrom} onChange={(e) => setPendingFilters(p => ({ ...p, dateFrom: e.target.value }))} />
                </label>
                <label className="notion-date-field-label">
                  <span>إلى</span>
                  <input type="date" value={pendingFilters.dateTo} onChange={(e) => setPendingFilters(p => ({ ...p, dateTo: e.target.value }))} />
                </label>
              </div>
            </div>
            <div className="notion-filter-row">
              <label>{t('offers.deedType')}</label>
              <select value={pendingFilters.deedType} onChange={(e) => setPendingFilters(p => ({ ...p, deedType: e.target.value }))}>
                <option value="all">{t('common.all')}</option>
                <option value="free">{t('offers.deedFree')}</option>
                <option value="mortgaged">{t('offers.deedMortgaged')}</option>
              </select>
            </div>
            <div className="notion-filter-row">
              <label>{t('offers.offerNature')}</label>
              <select value={pendingFilters.offerNature} onChange={(e) => setPendingFilters(p => ({ ...p, offerNature: e.target.value }))}>
                <option value="all">{t('common.all')}</option>
                <option value="direct">{t('offers.natureDirectWithOwner')}</option>
                <option value="withDirect">{t('offers.natureWithDirect')}</option>
                <option value="multiple">{t('offers.natureWithMe2OrMore')}</option>
              </select>
            </div>
            <div className="notion-filter-row">
              <label>{t('offers.status')}</label>
              <select value={pendingFilters.status} onChange={(e) => setPendingFilters(p => ({ ...p, status: e.target.value }))}>
                <option value="all">{t('common.all')}</option>
                <option value="updated">محدث</option>
                <option value="not_updated">غير محدث</option>
              </select>
            </div>
            <div className="notion-filter-row">
              <label>عرض خاص</label>
              <select value={pendingFilters.network} onChange={(e) => setPendingFilters(p => ({ ...p, network: e.target.value }))}>
                <option value="">الكل</option>
                <option value="true">عرض عام</option>
                <option value="false">عرض خاص</option>
              </select>
            </div>
            </div>
          </div>
        </div>
      )}

      {showMobileSortSheet && (
        <div className="notion-sheet-backdrop" onClick={() => setShowMobileSortSheet(false)}>
          <div className="notion-sort-panel" onClick={(e) => e.stopPropagation()}>
            <div className="notion-filters-header">
              <span>الفرز</span>
            </div>
            <div className="notion-sort-options">
              {[
                { value: 'newest', label: 'الأحدث أولاً' },
                { value: 'oldest', label: 'الأقدم أولاً' },
                { value: 'price_high', label: 'السعر (الأعلى أولاً)' },
                { value: 'price_low', label: 'السعر (الأقل أولاً)' },
                { value: 'area_large', label: 'المساحة (الأكبر أولاً)' },
                { value: 'area_small', label: 'المساحة (الأصغر أولاً)' }
              ].map((option) => (
                <button
                  key={option.value}
                  className={`sort-option-btn ${sortBy === option.value ? 'active' : ''}`}
                  onClick={() => {
                    setSortBy(option.value);
                    setShowMobileSortSheet(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className={`mobile-fab-stack ${showMobileFabMenu ? 'open' : ''}`}>
        <div className="mobile-fab-menu" role="menu" aria-label="إجراءات سريعة">
          <button className="mobile-fab-action" onClick={() => handleExportOffers('xlsx')} disabled={exportingOffers}>
            <Download size={16} />
            <span>تصدير Excel</span>
          </button>
          <button className="mobile-fab-action" onClick={() => handleExportOffers('pdf')} disabled={exportingOffers}>
            <Download size={16} />
            <span>تصدير PDF</span>
          </button>
          <button className="mobile-fab-action" onClick={handleCopyFormLink}>
            <LinkIcon size={16} />
            <span>{copiedLink ? t('common.copied') : 'نسخ الرابط'}</span>
          </button>
          <PermissionGate
            feature="LIMIT_OFFERS"
            type="limit"
            currentCount={offers.length}
            hideWhenDenied={false}
            fallback={
              <button className="mobile-fab-action" disabled title={t('permissions.limitReached')}>
                <Plus size={16} />
                <span>{t('permissions.limitReached')}</span>
              </button>
            }
          >
            <button
              className="mobile-fab-action"
              onClick={() => navigate('/offers/new', { state: { from: '/offers' } })}
            >
              <Plus size={16} />
              <span>{t('offers.addOffer')}</span>
            </button>
          </PermissionGate>
        </div>

        <button
          className="mobile-fab-main"
          onClick={() => setShowMobileFabMenu(!showMobileFabMenu)}
          aria-label="إجراءات"
        >
          {showMobileFabMenu ? <X size={18} /> : <Plus size={20} />}
        </button>
      </div>

      {/* بانر تحذير العروض غير المحدثة */}
      {notUpdatedOffers > 0 && (
        <div className="not-updated-banner">
          <span className="not-updated-banner-text">
            لديك <strong>{notUpdatedOffers}</strong> عرض غير محدث — هذه العروض لا تظهر في نتائج المطابقة
          </span>
          <button
            className="not-updated-banner-btn"
            onClick={() => setFilterStatus('not_updated')}
          >
            عرض الغير محدثة
          </button>
        </div>
      )}

      {filteredOffers.length === 0 ? (
        <div className="empty-state">
          <Building2 size={64} />
          <h3>{t('offers.noOffers')}</h3>
          <p>{t('offers.addFirstOffer')}</p>
          <button className="btn btn-primary" onClick={() => navigate('/offers/new', { state: { from: '/offers' } })}>
            <Plus size={20} />
            {t('offers.addOffer')}
          </button>
        </div>
      ) : viewMode === 'gallery' ? (
        <div className="offers-gallery-grid private-gallery">
          {filteredOffers.map(offer => (
            <div key={offer._id || offer.id} className="offer-gallery-card" onClick={() => handleOpenView(offer)}>
              <div className="offer-gallery-image">
                {getOfferImage(offer) ? (
                  <img src={getOfferImage(offer)} alt={offer.title} crossOrigin="anonymous" />
                ) : (
                  <div className="no-image-placeholder">
                    <img src={user?.avatar || '/mesk-brand-icon.svg'} alt="" className={user?.avatar ? 'offer-thumb-bg' : 'offer-thumb-logo'} />
                  </div>
                )}
                <span className="offer-gallery-price">{formatPrice(offer.price, 'SAR', { onRequest: offer.priceOnRequest })}</span>
                <div className="offer-gallery-badges">
                  <span className="type-badge-sm" style={getPropertyTypeColor(offer.propertyType || offer.type)}>
                    {propertyTypes.find(t => t.value === (offer.propertyType || offer.type))?.label}
                  </span>
                  {networkEnabled && !offer.showInNetwork && (
                    <span className="private-badge-sm" title="عرض خاص"><Network size={10} /></span>
                  )}
                </div>
              </div>
              <div className="offer-gallery-info" onClick={(e) => e.stopPropagation()}>
                <h4>{offer.title || 'بدون عنوان'}</h4>
                <span><MapPin size={12} /> {offer.city} - {offer.district}</span>
                {offer.area > 0 && <span><Maximize size={12} /> {formatArea(offer.area)}</span>}
                <div className="offer-gallery-actions">
                  <button className="action-btn" title="عرض" onClick={() => handleOpenView(offer)}><Eye size={15} /></button>
                  <button className="action-btn" title="تعديل" onClick={() => handleOpenEdit(offer)}><Edit size={15} /></button>
                  <DropdownMenu
                    isOpen={showMoreMenu === (offer._id || offer.id)}
                    onClose={() => setShowMoreMenu(null)}
                    trigger={
                      <button className="action-btn" onClick={() => setShowMoreMenu(showMoreMenu === (offer._id || offer.id) ? null : (offer._id || offer.id))}>
                        <MoreVertical size={15} />
                      </button>
                    }
                  >
                    <button onClick={() => { handleShareOffer(offer); setShowMoreMenu(null); }}><Share2 size={16} /> مشاركة</button>
                    <button onClick={() => { handleDuplicateOffer(offer); setShowMoreMenu(null); }}><Plus size={16} /> تكرار</button>
                    {!(isSub && subPerms.deleteListings === false) && (
                      <button className="delete-option" onClick={() => { setSelectedOffer(offer); setShowDeleteModal(true); setShowMoreMenu(null); }}><Trash2 size={16} /> حذف</button>
                    )}
                  </DropdownMenu>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="offers-compact-grid">
          {filteredOffers.map(offer => (
            <div key={offer._id || offer.id} className="offer-compact-card" onClick={() => handleOpenView(offer)}>
              <div className="offer-compact-thumb">
                {getOfferImage(offer) ? (
                  <img src={getOfferImage(offer)} alt={offer.title} />
                ) : (
                  <div className="no-image-placeholder">
                    <img
                      src={user?.avatar || '/mesk-brand-icon.svg'}
                      alt=""
                      className={user?.avatar ? 'offer-thumb-bg' : 'offer-thumb-logo'}
                    />
                    <span className="no-img-text">لا توجد صورة</span>
                  </div>
                )}
                <div className="offer-compact-badges">
                  <span className="type-badge-sm" style={getPropertyTypeColor(offer.propertyType || offer.type)}>
                    {propertyTypes.find(t => t.value === (offer.propertyType || offer.type))?.label}
                  </span>
                  {networkEnabled && !offer.showInNetwork && (
                    <span className="private-badge-sm" title="عرض خاص"><Network size={10} /></span>
                  )}
                </div>
              </div>
              <div className="offer-compact-details">
                <div className="offer-compact-top">
                  <h4 className="offer-compact-title">{offer.title}</h4>
                  <div className="offer-compact-actions" onClick={(e) => e.stopPropagation()}>
                    {offer.ownerPhone && (
                      <button className="action-btn whatsapp-btn" title="واتساب" onClick={() => {
                        let phone = offer.ownerPhone.replace(/[\s-]/g, '');
                        if (phone.startsWith('0')) phone = '966' + phone.slice(1);
                        else if (!phone.startsWith('966') && !phone.startsWith('+966')) phone = '966' + phone;
                        phone = phone.replace('+', '');
                        window.open(`https://wa.me/${phone}`, '_blank');
                      }}>
                        <FaWhatsapp size={16} />
                      </button>
                    )}
                    <DropdownMenu
                      isOpen={showMoreMenu === (offer._id || offer.id)}
                      onClose={() => setShowMoreMenu(null)}
                      trigger={
                        <button className="action-btn" onClick={() => setShowMoreMenu(showMoreMenu === (offer._id || offer.id) ? null : (offer._id || offer.id))}>
                          <MoreVertical size={16} />
                        </button>
                      }
                    >
                      <button onClick={() => { handleOpenView(offer); setShowMoreMenu(null); }}>
                        <Eye size={16} /> {t('common.view')}
                      </button>
                      <button onClick={() => { handleOpenEdit(offer); setShowMoreMenu(null); }}>
                        <Edit size={16} /> {t('common.edit')}
                      </button>
                      {networkEnabled && (
                      <button onClick={() => { handleToggleNetwork(offer); setShowMoreMenu(null); }}>
                        <Network size={16} /> {offer.showInNetwork ? 'عرض خاص' : 'إلغاء الخصوصية'}
                      </button>
                      )}
                      <button onClick={() => { handleShareOffer(offer); setShowMoreMenu(null); }}>
                        <Share2 size={16} /> مشاركة
                      </button>
                      <button onClick={() => { handleDuplicateOffer(offer); setShowMoreMenu(null); }}>
                        <Plus size={16} /> تكرار
                      </button>
                      {!(isSub && subPerms.deleteListings === false) && (
                      <button className="delete-option" onClick={() => { setSelectedOffer(offer); setShowDeleteModal(true); setShowMoreMenu(null); }}>
                        <Trash2 size={16} /> حذف
                      </button>
                      )}
                    </DropdownMenu>
                  </div>
                </div>
                <div className="offer-compact-price">{formatPrice(offer.price, 'SAR', { onRequest: offer.priceOnRequest })}{offer.offerType === 'rent' ? ' / سنوياً' : ''}</div>
                <div className="offer-compact-meta">
                  <span><MapPin size={12} /> {offer.city} - {offer.district}</span>
                  {offer.area > 0 && <span><Maximize size={12} /> {formatArea(offer.area)}</span>}
                </div>
                {(() => {
                    const missingFields = [];
                    if (!offer.propertyType && !offer.type) missingFields.push('نوع العقار');
                    if (!offer.city) missingFields.push('المدينة');
                    if (!offer.district) missingFields.push('الحي');
                    if (!offer.price && !offer.priceOnRequest) missingFields.push('السعر');
                    if (!offer.title) missingFields.push('العنوان');
                    if (!offer.area) missingFields.push('المساحة');
                    return missingFields.length > 0 ? (
                      <div className="missing-fields-indicator" title={`حقول ناقصة: ${missingFields.join('، ')}`}>
                        <span className="missing-icon">⚠</span>
                        <span className="missing-text">{missingFields.length} حقول ناقصة</span>
                      </div>
                    ) : null;
                })()}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="offers-list">
          {filteredOffers.map(offer => (
            <div key={offer._id || offer.id} className="offer-list-item" onClick={() => handleOpenView(offer)}>
              <div className="offer-image-small">
                {getOfferImage(offer) ? (
                  <img src={getOfferImage(offer)} alt={offer.title} />
                ) : (
                  <div className="no-image-placeholder">
                    <img
                      src={user?.avatar || '/mesk-brand-icon.svg'}
                      alt=""
                      className={user?.avatar ? 'offer-thumb-bg' : 'offer-thumb-logo'}
                    />
                    <span className="no-img-text">لا توجد صورة</span>
                  </div>
                )}
              </div>
              <div className="offer-info">
                <div className="offer-badges-inline">
                  <span 
                    className="type-badge"
                    style={getPropertyTypeColor(offer.propertyType || offer.type)}
                  >
                    {propertyTypes.find(t => t.value === (offer.propertyType || offer.type))?.label}
                  </span>
                  <span 
                    className="status-badge"
                    style={getStatusColor(offer.status)}
                  >
                    {offer.status === 'updated' ? 'محدث' : 'غير محدث'}
                  </span>
                </div>
                <h3>{offer.title}</h3>
                <div className="offer-meta">
                  <span><MapPin size={14} /> {offer.city} - {offer.district}</span>
                  {offer.area && <span><Maximize size={14} /> {formatArea(offer.area)}</span>}
                </div>
                <span className="offer-price-inline">
                  {formatPrice(offer.price, 'SAR', { onRequest: offer.priceOnRequest })}
                  {offer.offerType === 'rent' ? ' / سنوياً' : ''}
                </span>
              </div>
              <div className="offer-price-col">
                <span className="price">{formatPrice(offer.price, 'SAR', { onRequest: offer.priceOnRequest })}</span>
                {offer.offerType === 'rent' && <span className="period">/ سنوياً</span>}
              </div>
              <div className="offer-actions-col" onClick={(e) => e.stopPropagation()}>
                {offer.ownerPhone && (
                  <button className="action-btn whatsapp-btn" title="تواصل واتساب مع صاحب العرض" onClick={() => {
                    let phone = offer.ownerPhone.replace(/[\s-]/g, '');
                    if (phone.startsWith('0')) phone = '966' + phone.slice(1);
                    else if (!phone.startsWith('966') && !phone.startsWith('+966')) phone = '966' + phone;
                    phone = phone.replace('+', '');
                    window.open(`https://wa.me/${phone}`, '_blank');
                  }}>
                    <FaWhatsapp size={18} />
                  </button>
                )}
                <DropdownMenu
                  isOpen={showMoreMenu === (offer._id || offer.id)}
                  onClose={() => setShowMoreMenu(null)}
                  trigger={
                    <button className="action-btn" onClick={() => setShowMoreMenu(showMoreMenu === (offer._id || offer.id) ? null : (offer._id || offer.id))}>
                      <MoreVertical size={18} />
                    </button>
                  }
                >
                  <button onClick={() => { handleOpenView(offer); setShowMoreMenu(null); }}>
                    <Eye size={16} /> {t('common.view')}
                  </button>
                  <button onClick={() => { handleOpenEdit(offer); setShowMoreMenu(null); }}>
                    <Edit size={16} /> {t('common.edit')}
                  </button>
                  {networkEnabled && (
                  <button onClick={() => { handleToggleNetwork(offer); setShowMoreMenu(null); }}>
                    <Network size={16} /> {offer.showInNetwork ? 'عرض خاص' : 'إلغاء الخصوصية'}
                  </button>
                  )}
                  <button onClick={() => { handleShareOffer(offer); setShowMoreMenu(null); }}>
                    <Share2 size={16} /> مشاركة
                  </button>
                  <button onClick={() => { handleDuplicateOffer(offer); setShowMoreMenu(null); }}>
                    <Plus size={16} /> تكرار
                  </button>
                  {!(isSub && subPerms.deleteListings === false) && (
                  <button className="delete-option" onClick={() => { setSelectedOffer(offer); setShowDeleteModal(true); setShowMoreMenu(null); }}>
                    <Archive size={16} /> حذف
                  </button>
                  )}
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      )}

      {pagination.total > 1 && (
        <GooglePagination
          currentPage={pagination.current}
          totalPages={pagination.total}
          onPageChange={(page) => setPagination(prev => ({ ...prev, current: page }))}
          prevLabel={t('common.previous')}
          nextLabel={t('common.next')}
          infoPrefix={t('common.page')}
          infoMiddle={t('common.of')}
          isRTL={currentLang === 'ar'}
        />
      )}

      </>
      )}

      {creationMode && (
        <div className="new-offer-page-shell">
          <div className="modal-content large new-offer-page-card" data-tour="offer-form">
            <div className="modal-header">
              <h2>
                <Plus size={24} />
                إضافة عرض جديد
              </h2>
              {/* Keep the existing back behavior while using a compact page-style label. */}
              <button className="btn btn-outline new-offer-cancel-btn" onClick={handleCloseAddPage} aria-label="الرجوع">
                <X size={18} />
                رجوع
              </button>
            </div>
            
            <div className="modal-body">
              {currentStep === 0 && (
                <div className="add-method-choice">
                  <button
                    className="choice-btn choice-btn-ai"
                    onClick={() => { setAddMethod('ai'); setCurrentStep('ai'); }}
                  >
                    <Sparkles size={22} />
                    إضافة بالذكاء الاصطناعي
                  </button>
                  <button
                    className="choice-btn choice-btn-form"
                    onClick={() => { setAddMethod('form'); setCurrentStep(1); }}
                  >
                    <Edit size={22} />
                    إضافة يدوي
                  </button>
                </div>
              )}

              {/* 5 steps: العقار والموقع → التفاصيل → معلومات خاصة → الوسائط → مراجعة */}
              {typeof currentStep === 'number' && currentStep >= 1 && (
                <div className="step-indicator">
                  {[
                    { num: '١', label: 'العقار' },
                    { num: '٢', label: 'التفاصيل' },
                    { num: '٣', label: 'خاص' },
                    { num: '٤', label: 'وسائط' },
                    { num: '٥', label: 'مراجعة' },
                  ].map((step, index) => {
                    const stepNum = index + 1;
                    return (
                      <div
                        key={index}
                        className={`step-item ${currentStep === stepNum ? 'active' : ''} ${currentStep > stepNum ? 'done' : ''} clickable`}
                        onClick={() => setCurrentStep(stepNum)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="step-dot">{step.num}</span>
                        <span className="step-label">{step.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {addMethod === 'form' && (
                <div className="offer-form">

                  {/* ===== STEP 1: العقار والموقع — نوع + عنوان + مدينة + حي + showInNetwork فقط ===== */}
                  {currentStep === 1 && (
                  <>
                  {/* نوع العقار + نوع العرض جنباً لجنب */}
                  <div className="form-row two-cols form-row-pair">
                    <div className="form-group">
                      <div className="field-label-row">
                        <label className="form-label-text">{t('offers.propertyType')} <span className="required">*</span></label>
                      </div>
                      <select
                        className="form-control"
                        value={newOffer.type}
                        onChange={(e) => setNewOffer({ ...newOffer, type: e.target.value })}
                      >
                        <option value="">اختر نوع العقار</option>
                        {propertyTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <div className="field-label-row">
                        <label className="form-label-text">{t('offers.offerType')} <span className="required">*</span></label>
                      </div>
                      <select
                        className="form-control"
                        value={newOffer.offerType}
                        onChange={(e) => setNewOffer({ ...newOffer, offerType: e.target.value })}
                      >
                        <option value="">{t('offers.offerType')}</option>
                        <option value="sale">{t('offers.sale')}</option>
                        <option value="rent">{t('offers.rent')}</option>
                      </select>
                    </div>
                  </div>

                  {/* العنوان */}
                  <div className="form-row">
                    <div className="form-group">
                      <div className="field-label-row">
                        <label className="form-label-text">{t('offers.title_field')} <span className="required">*</span></label>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        value={newOffer.title}
                        onChange={(e) => setNewOffer({ ...newOffer, title: e.target.value })}
                        placeholder={t('offers.title_field')}
                        required
                      />
                    </div>
                  </div>

                  {/* المدينة + الحي */}
                  <div className="form-row two-cols">
                    <div className="form-group">
                      <div className="field-label-row">
                        <label className="form-label-text">{t('offers.city')} <span className="required">*</span></label>
                      </div>
                      <SearchableSelect
                        options={sortedCities.map(city => ({ value: city.label, label: city.label }))}
                        value={newOffer.city}
                        onChange={(value) => setNewOffer({ ...newOffer, city: value, region: '', district: '' })}
                        placeholder={t('common.selectCity')}
                        searchPlaceholder="ابحث عن المدينة..."
                        loading={loadingLocations}
                        disabled={loadingLocations}
                      />
                    </div>
                    <div className="form-group">
                      <div className="field-label-row">
                        <label className="form-label-text">{t('offers.district')} <span className="required">*</span></label>
                      </div>
                      <SearchableSelect
                        options={getAllDistrictsForCity(newOffer.city).map(d => ({ value: d, label: d }))}
                        value={newOffer.district}
                        onChange={(value) => setNewOffer({ ...newOffer, district: value })}
                        placeholder="اختر الحي"
                        searchPlaceholder="ابحث عن الحي..."
                        disabled={!newOffer.city || loadingLocations}
                      />
                    </div>
                  </div>

                  {/* showInNetwork في آخر Step 1 — يظهر فقط عندما تكون ميزة السوق مفعّلة */}
                  {networkEnabled && (
                  <div className="form-row">
                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={!newOffer.showInNetwork}
                          onChange={(e) => setNewOffer({ ...newOffer, showInNetwork: !e.target.checked })}
                        />
                        <span>{t('offers.showInNetwork')}</span>
                      </label>
                    </div>
                  </div>
                  )}
                  </>
                  )}

                  {/* ===== STEP 2: التفاصيل — سعر + مساحة + صك + دفعات + أرض + كهرباء/ماء + وصف + حقول اختيارية ===== */}
                  {currentStep === 2 && (
                  <>
                  {/* السعر + المساحة */}
                  <div className="form-row two-cols">
                    <div className="form-group">
                      <div className="field-label-row">
                        <label className="form-label-text">{t('offers.price')} (ر.س) <span className="required">*</span></label>
                        <div className="field-label-actions">
                          <label className="later-checkbox"><input type="checkbox" checked={newOffer.priceOnRequest} onChange={(e) => togglePriceOnRequest(e.target.checked)} /><span>على السوم</span></label>
                        </div>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        value={formatNumberWithCommas(newOffer.price)}
                        onChange={(e) => setNewOffer({ ...newOffer, price: parseFormattedNumber(e.target.value) })}
                        placeholder={newOffer.priceOnRequest ? 'على السوم' : t('offers.price')}
                        disabled={newOffer.priceOnRequest}
                      />
                    </div>
                    <div className="form-group">
                      <div className="field-label-row">
                        <label className="form-label-text">
                          {t('offers.area')} (م²) <span className="required">*</span>
                        </label>
                      </div>
                      <input
                        type="number"
                        className="form-control"
                        value={newOffer.area}
                        onChange={(e) => setNewOffer({ ...newOffer, area: e.target.value })}
                        placeholder={t('offers.area')}
                      />
                    </div>
                  </div>

                  {/* نوع الصك — للبيع فقط (غير أرض) */}
                  {newOffer.type !== 'land' && newOffer.offerType === 'sale' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>{t('offers.deedType')}</label>
                        <select
                          className="form-control"
                          value={newOffer.deedType}
                          onChange={(e) => setNewOffer({ ...newOffer, deedType: e.target.value })}
                        >
                          <option value="">{t('offers.deedType')}</option>
                          <option value="free">{t('offers.deedFree')}</option>
                          <option value="mortgaged">{t('offers.deedMortgaged')}</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* عدد الدفعات — للإيجار فقط (غير أرض) */}
                  {newOffer.type !== 'land' && newOffer.offerType === 'rent' && (
                    <div className="form-row two-cols">
                      <div className="form-group">
                        <label>كم دفعة</label>
                        <input
                          type="number"
                          className="form-control"
                          value={newOffer.acceptsPayments}
                          onChange={(e) => setNewOffer({ ...newOffer, acceptsPayments: e.target.value })}
                          placeholder="عدد الدفعات"
                          min="1"
                        />
                      </div>
                      <div className="form-group" />
                    </div>
                  )}

                  {/* غرض الأرض + نوع الصك — للأرض فقط */}
                  {newOffer.type === 'land' && (
                    <div className="form-row two-cols">
                      <div className="form-group">
                        <label>{t('offers.landPurpose')} <span className="required">*</span></label>
                        <select
                          className="form-control"
                          value={newOffer.landPurpose}
                          onChange={(e) => setNewOffer({ ...newOffer, landPurpose: e.target.value })}
                        >
                          <option value="">{t('offers.landPurpose')}</option>
                          <option value="residential">{t('offers.purposeResidential')}</option>
                          <option value="agricultural">{t('offers.purposeAgricultural')}</option>
                          <option value="industrial">{t('offers.purposeIndustrial')}</option>
                          <option value="commercial">{t('offers.purposeCommercial')}</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>{t('offers.deedType')}</label>
                        <select
                          className="form-control"
                          value={newOffer.deedType}
                          onChange={(e) => setNewOffer({ ...newOffer, deedType: e.target.value })}
                        >
                          <option value="">{t('offers.deedType')}</option>
                          <option value="free">{t('offers.deedFree')}</option>
                          <option value="mortgaged">{t('offers.deedMortgaged')}</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* الكهرباء + الماء — للإيجار (غير أرض) */}
                  {newOffer.type !== 'land' && newOffer.offerType === 'rent' && (
                    <div className="form-row two-cols">
                      <div className="form-group">
                        <label>{t('offers.electricity')}</label>
                        <select
                          className="form-control"
                          value={newOffer.electricity}
                          onChange={(e) => setNewOffer({ ...newOffer, electricity: e.target.value })}
                        >
                          <option value="">{t('offers.electricity')}</option>
                          <option value="independent">{t('offers.independent')}</option>
                          <option value="shared">{t('offers.shared')}</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>{t('offers.water')}</label>
                        <select
                          className="form-control"
                          value={newOffer.water}
                          onChange={(e) => setNewOffer({ ...newOffer, water: e.target.value })}
                        >
                          <option value="">{t('offers.water')}</option>
                          <option value="independent">{t('offers.independent')}</option>
                          <option value="shared">{t('offers.shared')}</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* الوصف */}
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('offers.description')}</label>
                      <textarea
                        className="form-control auto-resize-textarea"
                        rows={3}
                        value={newOffer.description}
                        onChange={(e) => {
                          setNewOffer({ ...newOffer, description: e.target.value });
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                        ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                        placeholder={t('offers.description')}
                      />
                    </div>
                  </div>

                  {/* زر "اظهار المزيد" + الحقول الاختيارية */}
                  <button
                    type="button"
                    className="btn btn-outline show-more-fields-btn"
                    onClick={() => setShowMoreOfferFields(!showMoreOfferFields)}
                    style={{ width: '100%', marginBottom: '16px', marginTop: '8px' }}
                  >
                    {showMoreOfferFields ? t('common.showLess', 'إخفاء الحقول الاختيارية') : t('common.showMore', 'اظهار المزيد من الحقول')}
                  </button>

                  {showMoreOfferFields && (<>
                  <div className="form-row three-cols">
                    <div className="form-group">
                      <label>{t('offers.status')}</label>
                      <select
                        className="form-control"
                        value={newOffer.status}
                        onChange={(e) => setNewOffer({ ...newOffer, status: e.target.value })}
                      >
                        <option value="updated">محدث</option>
                        <option value="not_updated">غير محدث</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('offers.offerNature')}</label>
                      <select
                        className="form-control"
                        value={newOffer.offerNature}
                        onChange={(e) => setNewOffer({ ...newOffer, offerNature: e.target.value })}
                      >
                        <option value="">{t('offers.offerNature')}</option>
                        <option value="direct">{t('offers.natureDirectWithOwner', 'مباشر')}</option>
                        <option value="withDirect">{t('offers.natureWithDirect', 'مع المباشر')}</option>
                        <option value="multiple">{t('offers.natureWithMe2OrMore', 'معي 2 او اكثر')}</option>
                      </select>
                    </div>
                    <div className="form-group" />
                  </div>

                  {/* الغرف + الحمامات + الأدوار — للعقارات غير الأرض */}
                  {newOffer.type !== 'land' && (
                    <div className="form-row three-cols">
                      <div className="form-group">
                        <label>{t('offers.rooms')}</label>
                        <input
                          type="number"
                          className="form-control"
                          value={newOffer.rooms}
                          onChange={(e) => setNewOffer({ ...newOffer, rooms: e.target.value })}
                          placeholder={t('offers.rooms')}
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('offers.bathrooms')}</label>
                        <input
                          type="number"
                          className="form-control"
                          value={newOffer.bathrooms}
                          onChange={(e) => setNewOffer({ ...newOffer, bathrooms: e.target.value })}
                          placeholder={t('offers.bathrooms')}
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('offers.floors')}</label>
                        <input
                          type="number"
                          className="form-control"
                          value={newOffer.floors}
                          onChange={(e) => setNewOffer({ ...newOffer, floors: e.target.value })}
                          placeholder={t('offers.floors')}
                          min="0"
                        />
                      </div>
                    </div>
                  )}

                  {/* الاتجاه + عرض الشارع — للعقارات غير الأرض */}
                  {newOffer.type !== 'land' && (
                    <div className="form-row two-cols">
                      <div className="form-group">
                        <label>{t('offers.direction')}</label>
                        <select
                          className="form-control"
                          value={newOffer.direction}
                          onChange={(e) => setNewOffer({ ...newOffer, direction: e.target.value })}
                        >
                          <option value="">{t('offers.selectDirection')}</option>
                          <option value="north">{t('offers.north')}</option>
                          <option value="south">{t('offers.south')}</option>
                          <option value="east">{t('offers.east')}</option>
                          <option value="west">{t('offers.west')}</option>
                          <option value="northeast">{t('offers.northeast')}</option>
                          <option value="northwest">{t('offers.northwest')}</option>
                          <option value="southeast">{t('offers.southeast')}</option>
                          <option value="southwest">{t('offers.southwest')}</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>{t('offers.street')} (م)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={newOffer.street}
                          onChange={(e) => setNewOffer({ ...newOffer, street: e.target.value })}
                          placeholder="عرض الشارع (م)"
                          min="0"
                        />
                      </div>
                    </div>
                  )}

                  {/* العمر */}
                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>{t('offers.age')} (سنة)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={newOffer.age}
                        onChange={(e) => setNewOffer({ ...newOffer, age: e.target.value })}
                        placeholder="العمر (سنة)"
                      />
                    </div>
                    <div className="form-group" />
                  </div>

                  {/* حقول الأرض الإضافية — سعر المتر + الشارع + الاتجاه */}
                  {newOffer.type === 'land' && (
                    <div className="form-row three-cols">
                      <div className="form-group">
                        <label>{t('offers.pricePerMeter')} (ر.س/م²)</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formatNumberWithCommas(newOffer.pricePerMeter)}
                          onChange={(e) => handleOwnerNameChange(e.target.value, setNewOffer)}
                          placeholder={t('offers.pricePerMeter')}
                          list="previous-owners-list"
                          autoComplete="off"
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('offers.street')} (م)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={newOffer.street}
                          onChange={(e) => setNewOffer({ ...newOffer, street: e.target.value })}
                          placeholder={t('offers.street')}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('offers.direction')}</label>
                        <select
                          className="form-control"
                          value={newOffer.direction}
                          onChange={(e) => setNewOffer({ ...newOffer, direction: e.target.value })}
                        >
                          <option value="">{t('offers.selectDirection')}</option>
                          <option value="north">{t('offers.north')}</option>
                          <option value="south">{t('offers.south')}</option>
                          <option value="east">{t('offers.east')}</option>
                          <option value="west">{t('offers.west')}</option>
                          <option value="northeast">{t('offers.northeast')}</option>
                          <option value="northwest">{t('offers.northwest')}</option>
                          <option value="southeast">{t('offers.southeast')}</option>
                          <option value="southwest">{t('offers.southwest')}</option>
                        </select>
                      </div>
                    </div>
                  )}

                  </>)}
                  </>
                  )}

                  {/* ===== STEP 3: معلومات خاصة — بيانات المالك + ملاحظات + مستندات خاصة ===== */}
                  {currentStep === 3 && (
                  <>
                  {/* تنبيه: هذه المعلومات خاصة بك ولا تُعرض للجمهور */}
                  <div className="private-fields-note">
                    <span>🔒</span>
                    <div>
                      <strong>معلومات خاصة بك فقط</strong>
                      <p>هذه البيانات لا تظهر للزبائن أو أي شخص آخر — أنت وحدك من يراها</p>
                    </div>
                  </div>

                  {/* اسم صاحب العرض + رقم الجوال */}
                  <div className="form-row two-cols" ref={ownerDropdownRef}>
                    <div className="form-group owner-suggest-wrap">
                      <label>{t('offers.ownerName')}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newOffer.ownerName}
                        onChange={(e) => handleOwnerInputChange('name', e.target.value, setNewOffer)}
                        onFocus={() => handleOwnerFocus('name')}
                        placeholder={t('offers.ownerName')}
                        autoComplete="off"
                      />
                      {ownerDropdownOpen && ownerDropdownField === 'name' && ownerSuggestions.length > 0 && (
                        <ul className="owner-suggest-dropdown">
                          {ownerSuggestions.map((owner) => (
                            <li key={`${owner.ownerName}-${owner.ownerPhone}`} onMouseDown={() => handleSelectOwnerSuggestion(owner)}>
                              <span className="owner-suggest-name">{owner.ownerName}</span>
                              {owner.ownerPhone && <span className="owner-suggest-phone">{owner.ownerPhone}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="form-group owner-suggest-wrap">
                      <label>{t('offers.ownerPhone')} <small>({t('offers.phoneHint')})</small></label>
                      <input
                        type="tel"
                        className="form-control"
                        value={newOffer.ownerPhone}
                        onChange={(e) => handleOwnerInputChange('phone', e.target.value, setNewOffer)}
                        onFocus={() => handleOwnerFocus('phone')}
                        placeholder="+966 55 559 0799"
                        dir="ltr"
                        autoComplete="off"
                      />
                      {ownerDropdownOpen && ownerDropdownField === 'phone' && ownerSuggestions.length > 0 && (
                        <ul className="owner-suggest-dropdown">
                          {ownerSuggestions.map((owner) => (
                            <li key={`${owner.ownerName}-${owner.ownerPhone}`} onMouseDown={() => handleSelectOwnerSuggestion(owner)}>
                              <span className="owner-suggest-name">{owner.ownerName}</span>
                              {owner.ownerPhone && <span className="owner-suggest-phone">{owner.ownerPhone}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* الملاحظات الخاصة */}
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('offers.comment')}</label>
                      <textarea
                        className="form-control expandable-textarea"
                        rows={3}
                        value={newOffer.comment}
                        onChange={(e) => setNewOffer({ ...newOffer, comment: e.target.value })}
                        onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                        ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                        placeholder={t('offers.commentPlaceholder')}
                      />
                    </div>
                  </div>

                  {/* المستندات الخاصة — انتقلت من step الوسائط إلى هنا */}
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('offers.privateDocuments.title')}</label>
                      <div className="text-muted" style={{ marginBottom: '8px', fontSize: '12px' }}>
                        {t('offers.privateDocuments.ownerHint')}
                      </div>
                      <label className="upload-area" style={{ borderStyle: 'dashed' }}>
                        <Upload size={32} />
                        <p>{t('offers.privateDocuments.uploadAction')}</p>
                        <span>JPG, PNG, WEBP, PDF حتى 10MB</span>
                        <input type="file" accept="image/*,application/pdf" multiple onChange={handlePrivateFilesUpload} />
                      </label>
                      {privateFilesPreview.length > 0 && (
                        <div className="uploaded-preview">
                          {privateFilesPreview.map((item, idx) => (
                            <div key={`private-${idx}`} className="preview-item">
                              {item.previewUrl ? <img src={item.previewUrl} alt={item.name} /> : <div className="file-chip">PDF</div>}
                              <small style={{ display: 'block', padding: '4px 6px', fontSize: '11px' }}>{item.name}</small>
                              <button
                                type="button"
                                onClick={() => {
                                  setPrivateFiles(prev => prev.filter((_, i) => i !== idx));
                                  setPrivateFilesPreview(prev => prev.filter((_, i) => i !== idx));
                                }}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  </>
                  )}

                  {/* ===== STEP 4: الوسائط — صور + فيديوهات + روابط خارجية ===== */}
                  {currentStep === 4 && (
                  <>
                  {/* الصور — الصورة الأولى تصبح الغلاف */}
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('offers.images')} <small style={{color:'var(--text-secondary)', fontWeight:'normal'}}>— الصورة الأولى تصبح الصورة الرئيسية تلقائياً</small></label>
                      <label className="upload-area">
                        <Upload size={32} />
                        <p>اسحب الصور هنا أو اضغط للرفع</p>
                        <span>PNG, JPG حتى 5MB</span>
                        <input type="file" accept="image/*" multiple onChange={handleImageUpload} />
                      </label>
                      {uploadedImages.length > 0 && (
                        <div className="uploaded-preview">
                          {uploadedImages.map((img, idx) => (
                            <div key={idx} className="preview-item">
                              <img src={img} alt={`Preview ${idx + 1}`} />
                              <button type="button" onClick={() => {
                                setUploadedImages(prev => prev.filter((_, i) => i !== idx));
                                setImageFiles(prev => prev.filter((_, i) => i !== idx));
                                setNewOffer(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
                              }}><X size={14} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* الفيديوهات */}
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('offers.videos') || 'الفيديوهات'}</label>
                      <label className="upload-area">
                        <Upload size={32} />
                        <p>اسحب الفيديوهات هنا أو اضغط للرفع</p>
                        <span>MP4, WebM حتى 50MB</span>
                        <input type="file" accept="video/*" multiple onChange={handleVideoUpload} />
                      </label>
                      {newOffer.videos.length > 0 && (
                        <div className="uploaded-preview">
                          {newOffer.videos.map((video, idx) => (
                            <div key={idx} className="preview-item video-preview">
                              <video src={video} />
                              <span className="video-badge">فيديو {idx + 1}</span>
                              <button type="button" onClick={() => {
                                setVideoFiles(prev => prev.filter((_, i) => i !== idx));
                                setNewOffer(prev => ({ ...prev, videos: prev.videos.filter((_, i) => i !== idx) }));
                              }}><X size={14} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* الوسائط العامة هنا صور وفيديوهات فقط؛ المستندات تبقى خاصة في الخطوة السابقة. */}
                  {uploadProgress !== null && (
                    <div className="upload-progress-container">
                      <p className="upload-progress-label">جارٍ الرفع... {uploadProgress}%</p>
                      <div className="upload-progress-track">
                        <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
                      </div>
                    </div>
                  )}
                  </>
                  )}
                </div>
              )}

              {/* ===== STEP 5: مراجعة قبل الحفظ ===== */}
              {currentStep === 5 && (() => {
                const previewFields = [
                  { key: 'title', label: 'العنوان', value: newOffer.title, required: true, editType: 'text', step: 1 },
                  { key: 'type', label: 'نوع العقار', value: propertyTypes.find(p => p.value === newOffer.type)?.label || newOffer.type, required: true, editType: null, step: 1 },
                  { key: 'offerType', label: 'نوع العرض', value: newOffer.offerType === 'sale' ? 'بيع' : newOffer.offerType === 'rent' ? 'إيجار' : null, required: true, editType: null, step: 1 },
                  { key: 'city', label: 'المدينة', value: newOffer.city, required: true, editType: null, step: 1 },
                  { key: 'district', label: 'الحي', value: newOffer.district, required: true, editType: null, step: 1 },
                  { key: 'price', label: 'السعر', value: newOffer.priceOnRequest ? 'على السوم' : newOffer.price ? `${formatNumberWithCommas(newOffer.price)} ر.س` : null, required: true, editType: newOffer.priceOnRequest ? null : 'number', step: 2 },
                  { key: 'area', label: 'المساحة', value: newOffer.area ? `${newOffer.area} م²` : null, required: true, editType: 'number', suffix: ' م²', step: 2 },
                  { key: 'ownerName', label: 'صاحب العرض', value: newOffer.ownerName, required: false, editType: 'text', step: 3 },
                  { key: 'ownerPhone', label: 'رقم الجوال', value: newOffer.ownerPhone, required: false, editType: 'text', dir: 'ltr', step: 3 },
                  { key: 'age', label: 'العمر', value: newOffer.age ? `${newOffer.age} سنة` : null, required: false, editType: 'number', suffix: ' سنة', step: 2 },
                  { key: 'deedType', label: 'نوع الصك', value: newOffer.deedType === 'free' ? 'صك حر' : newOffer.deedType === 'mortgaged' ? 'مرهون' : null, required: false, editType: null, step: 2 },
                  { key: 'images', label: 'الصور', value: imageFiles.length > 0 ? `${imageFiles.length} صورة` : null, required: false, editType: null, step: 4 },
                ];
                return (
                <div className="offer-preview-step">
                  <div className="preview-step-header">
                    <Check size={22} />
                    <h3>مراجعة العرض قبل الحفظ</h3>
                    <p>اضغط على أي حقل لتعديله مباشرة</p>
                  </div>

                  <div className="preview-step-grid">
                    {previewFields.map(({ key, label, value, step, dir, required, editType, suffix }) => {
                      const isMissing = !value && required;
                      return (
                        <div key={key} className={`preview-card${isMissing ? ' preview-card-missing' : ''}`}>
                          <span className="preview-card-label">
                            {label}{isMissing && <span className="required-star"> *</span>}
                          </span>
                          {value && editType ? (
                            <input
                              className="preview-inline-input"
                              type={editType}
                              dir={dir || 'auto'}
                              defaultValue={editType === 'number' ? (newOffer[key] || '') : (newOffer[key] || '')}
                              onBlur={(e) => {
                                const val = editType === 'number' ? Number(e.target.value) : e.target.value;
                                setNewOffer(prev => ({ ...prev, [key]: val }));
                              }}
                            />
                          ) : value ? (
                            <span className="preview-card-value" dir={dir || 'auto'}>{value}</span>
                          ) : (
                            <button className="preview-add-btn" onClick={() => setCurrentStep(step)}>
                              {isMissing ? '⚠ أضف الآن' : '+ إضافة'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {newOffer.description && (
                    <div className="preview-description">
                      <span className="preview-card-label">الوصف</span>
                      <p>{newOffer.description}</p>
                    </div>
                  )}

                  <button
                    className="btn btn-outline preview-more-details-btn"
                    onClick={() => setCurrentStep(2)}
                  >
                    إضافة المزيد من التفاصيل
                  </button>
                </div>
                );
              })()}

              {currentStep === 'ai' && (
                <div className="ai-entry">
                  <div className="ai-header">
                    <Sparkles size={24} />
                    <h3>{t('offers.aiEntry')}</h3>
                    <p>{t('messages.analyzeDescription')}</p>
                  </div>
                  
                  {/* City selection before AI analysis */}
                  <div className="form-group ai-city-select">
                    <label>{t('offers.selectCityForAI')}</label>
                    <SearchableSelect
                      options={sortedCities.map(city => ({ value: city.label, label: city.label }))}
                      value={newOffer.city}
                      onChange={(value) => setNewOffer({ ...newOffer, city: value, district: '' })}
                      placeholder={loadingLocations ? t('common.loading') : t('offers.selectCityOptional')}
                      searchPlaceholder="ابحث عن المدينة..."
                      loading={loadingLocations}
                      disabled={loadingLocations}
                    />
                    <small className="hint">{t('offers.cityHintAI')}</small>
                  </div>

                  <div className="ai-textarea-wrap">
                    <textarea
                      className="form-control ai-textarea"
                      rows={8}
                      value={aiText}
                      onChange={(e) => {
                        setAiText(e.target.value);
                        setAiFailedMessage(null);
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                      placeholder={t('offers.aiPlaceholder')}
                    />
                    {aiText && (
                      <button
                        type="button"
                        className="clear-ai-btn"
                        onClick={() => { setAiText(''); setAiFailedMessage(null); }}
                      >
                        مسح
                      </button>
                    )}
                  </div>

                </div>
              )}
            </div>

            <div className="modal-footer step-footer">
              {(currentStep === 'ai' || (typeof currentStep === 'number' && currentStep >= 1)) && (
                <div className="step-nav-buttons">
                  {/* السابق: shown on 'ai' and all steps >= 1 */}
                  {(currentStep === 'ai' || (typeof currentStep === 'number' && currentStep >= 1)) && (
                    <button
                      className="btn btn-outline step-btn-prev"
                      onClick={() => currentStep === 'ai' ? setCurrentStep(0) : currentStep === 1 ? setCurrentStep(0) : setCurrentStep(s => s - 1)}
                      disabled={saving}
                    >
                      السابق
                    </button>
                  )}

                  {/* التالي: يُخفى في step 0 و step 5 (آخر خطوة) */}
                  {currentStep !== 0 && currentStep !== 5 && (
                    <button
                      className="btn btn-secondary step-btn-next"
                      onClick={async () => {
                        if (currentStep === 'ai') {
                          const success = await handleAnalyzeText();
                          if (success) setCurrentStep(5);
                          return;
                        }
                        if (currentStep === 1) {
                          if (!newOffer.type) { toast.warning('يرجى اختيار نوع العقار'); return; }
                          if (!newOffer.offerType) { toast.warning('يرجى اختيار نوع العرض'); return; }
                          if (!newOffer.city) { toast.warning('يرجى اختيار المدينة'); return; }
                          if (!newOffer.district) { toast.warning('يرجى اختيار الحي'); return; }
                        }
                        if (currentStep === 2) {
                          if (!newOffer.priceOnRequest && !newOffer.price) { toast.warning('يرجى إدخال السعر'); return; }
                          if (!newOffer.area || Number(newOffer.area) <= 0) { toast.warning('يرجى إدخال المساحة'); return; }
                        }
                        setCurrentStep(s => s + 1);
                      }}
                      disabled={saving || (currentStep === 'ai' && analyzing)}
                    >
                      {currentStep === 'ai' ? (
                        analyzing ? <><Loader size={16} className="spinner" /> جاري تحليل النص...</> : 'التالي'
                      ) : currentStep === 4 ? (
                        'مراجعة العرض'
                      ) : (
                        'التالي'
                      )}
                    </button>
                  )}

                  {/* حفظ: يظهر في step 5 فقط (خطوة المراجعة) */}
                  {currentStep === 5 && (
                    <button
                      className="btn btn-primary step-btn-save"
                      onClick={handleAddOffer}
                      disabled={saving || uploadProgress !== null}
                    >
                      {saving ? (
                        <><Loader size={16} className="spinner" /> جاري الحفظ...</>
                      ) : (
                        'حفظ العرض'
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {aiFailedMessage && (
        <div className="ai-failed-overlay" onClick={() => setAiFailedMessage(null)}>
          <div className="ai-failed-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ai-failed-icon">⚠️</div>
            <p className="ai-failed-title">حقول ناقصة</p>
            <p className="ai-failed-desc">{aiFailedMessage}</p>
            <p className="ai-failed-hint">
              {currentStep === 5
                ? 'تم تعبئة ما أمكن — أكمل الحقول الناقصة من صفحة المراجعة.'
                : 'يمكنك تعديل النص وإعادة المحاولة، أو الاستمرار وتعبئة الحقول الناقصة يدوياً.'}
            </p>
            <div className="ai-failed-actions">
              <button
                className="btn btn-outline"
                onClick={() => setAiFailedMessage(null)}
              >
                {currentStep === 5 ? 'حسناً' : 'تعديل النص'}
              </button>
              {currentStep !== 5 && (
                <button
                  className="btn btn-primary"
                  onClick={() => { setAiFailedMessage(null); setCurrentStep(5); }}
                >
                  استمرار وتعبئة يدوياً
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Trash2 size={24} />
                حذف العرض
              </h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p className="delete-message">هل تريد نقل هذا العرض إلى سلة المحذوفات؟ يمكنك استعادته خلال 30 يوماً.</p>
              {selectedOffer && (
                <div className="delete-preview">
                  {getOfferImage(selectedOffer) ? <img src={getOfferImage(selectedOffer)} alt={selectedOffer.title} /> : <div className="no-image-placeholder" style={{width: '80px', height: '60px', borderRadius: '8px'}}>لا توجد صور</div>}
                  <div>
                    <h4>{selectedOffer.title}</h4>
                    <p>{selectedOffer.city} - {selectedOffer.district}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowDeleteModal(false)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-danger" onClick={handleDeleteOffer}>
                <Trash2 size={18} />
                حذف
              </button>
            </div>
          </div>
        </div>
      )}

      {showUnsavedConfirmModal && creationMode && (
        <div className="modal-overlay" onClick={handleCancelUnsavedClose}>
          <div className="modal-content small unsaved-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-confirm-icon">
              <AlertTriangle size={22} />
            </div>
            <h3>لديك تغييرات غير محفوظة</h3>
            <p>يمكنك حفظ العرض أولاً ثم الإغلاق، أو الإغلاق بدون حفظ.</p>
            <div className="unsaved-confirm-actions">
              <button type="button" className="btn btn-outline" onClick={handleCancelUnsavedClose}>
                إلغاء
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDiscardAddChanges}>
                إغلاق بدون حفظ
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveAndCloseAddModal} disabled={saving}>
                {saving ? (
                  <>
                    <Loader size={16} className="spinner" />
                    جاري الحفظ...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    حفظ ثم إغلاق
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedOffer && (
        <div className="modal-overlay" onClick={() => { cancelNewOfferMatchFlow(); setShowViewModal(false); setSelectedOffer(null); setViewPrivateImages([]); setShowOfferMatches(false); setOfferMatches([]); setOfferMatchesError(''); setPreviewRequest(null); }}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>

            {/* ── 1. Floating close button (replaces sticky header) ── */}
            <button className="modal-close modal-close-floating" onClick={() => { cancelNewOfferMatchFlow(); setShowViewModal(false); setSelectedOffer(null); setViewPrivateImages([]); setShowOfferMatches(false); setOfferMatches([]); setOfferMatchesError(''); setPreviewRequest(null); }}>
              <X size={24} />
            </button>

            <div className="modal-body">
              <div className="view-offer-content">
                <div className="view-offer-details">
                  {/* One media viewer for images and videos instead of stacked grids. */}
                  <div className="view-offer-media">
                    {(() => {
                      const mediaItems = getOfferMediaItems(selectedOffer);
                      const total = mediaItems.length;
                      const safeIndex = total > 0 ? Math.min(viewMediaIndex, total - 1) : 0;
                      const activeMedia = mediaItems[safeIndex];

                      return (
                        <>
                          <div className="view-offer-image">
                            {activeMedia ? (
                              activeMedia.type === 'video' ? (
                                <video src={activeMedia.src} controls className="view-media-video" onDoubleClick={() => setFullscreenMedia({ items: mediaItems, index: safeIndex })} />
                              ) : (
                                <img src={activeMedia.src} alt={activeMedia.label} onClick={() => setFullscreenMedia({ items: mediaItems, index: safeIndex })} />
                              )
                            ) : (
                              <div className="no-image-placeholder" style={{width: '100%', height: '300px', borderRadius: '12px', fontSize: '16px'}}>لا توجد صور</div>
                            )}
                            <span className="img-overlay-badge img-overlay-type" style={{ background: selectedOffer.offerType === 'sale' ? '#3b82f6' : '#8b5cf6' }}>
                              {selectedOffer.offerType === 'sale' ? t('offers.sale') : t('offers.rent')}
                            </span>
                            <span className="img-overlay-dot" style={{ background: selectedOffer.status === 'updated' ? '#22c55e' : '#ef4444' }} title={selectedOffer.status === 'updated' ? 'محدث' : 'غير محدث'} />
                            {activeMedia && (
                              <button type="button" className="media-fullscreen-btn" onClick={() => setFullscreenMedia({ items: mediaItems, index: safeIndex })} aria-label="ملء الشاشة">
                                <Maximize size={18} />
                              </button>
                            )}
                            {total > 1 && (
                              <>
                                <button type="button" className="media-nav media-nav-prev" onClick={() => setViewMediaIndex((index) => Math.max(index - 1, 0))} disabled={safeIndex === 0} aria-label="السابق">&#8250;</button>
                                <button type="button" className="media-nav media-nav-next" onClick={() => setViewMediaIndex((index) => Math.min(index + 1, total - 1))} disabled={safeIndex === total - 1} aria-label="التالي">&#8249;</button>
                                <span className="media-counter">{safeIndex + 1} / {total}</span>
                              </>
                            )}
                          </div>

                          {total > 1 && (
                            <div className="media-thumbnails" aria-label="وسائط العرض">
                              {mediaItems.map((item, index) => (
                                <button type="button" key={`${item.type}-${index}`} className={`media-thumb ${index === safeIndex ? 'active' : ''}`} onClick={() => setViewMediaIndex(index)} aria-label={item.label}>
                                  {item.type === 'video' ? <span className="media-thumb-video">▶</span> : <img src={item.src} alt="" />}
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* ── 2. Unified media section ── */}
                  <div className="view-offer-image">
                    {getOfferImage(selectedOffer) ? <img src={getOfferImage(selectedOffer)} alt={selectedOffer.title} /> : <div className="no-image-placeholder" style={{width: '100%', height: '300px', borderRadius: '12px', fontSize: '16px'}}>لا توجد صور</div>}
                    {/* بيع/إيجار badge — top-right overlay */}
                    <span className="img-overlay-badge img-overlay-type" style={{ background: selectedOffer.offerType === 'sale' ? '#3b82f6' : '#8b5cf6' }}>
                      {selectedOffer.offerType === 'sale' ? t('offers.sale') : t('offers.rent')}
                    </span>
                    {/* Status dot — top-left overlay */}
                    <span className="img-overlay-dot" style={{ background: selectedOffer.status === 'updated' ? '#22c55e' : '#ef4444' }} title={selectedOffer.status === 'updated' ? 'محدث' : 'غير محدث'} />
                  </div>
                  {selectedOffer.images && selectedOffer.images.length > 0 && (
                    <div className="view-images-gallery">
                      <h4>الصور ({selectedOffer.images.length})</h4>
                      <div className="images-grid">
                        {selectedOffer.images.map((img, idx) => (
                          <div key={idx} className="gallery-item">
                            <img src={img} alt={`صورة ${idx + 1}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedOffer.videos && selectedOffer.videos.length > 0 && (
                    <div className="view-videos-gallery">
                      <h4>الفيديوهات ({selectedOffer.videos.length})</h4>
                      <div className="videos-grid">
                        {selectedOffer.videos.map((vid, idx) => (
                          <div key={idx} className="gallery-item video">
                            <video src={vid} controls />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── 3. Property type badge + title + location + price ── */}
                  <div className="view-badges">
                    <span className="type-badge" style={getPropertyTypeColor(selectedOffer.propertyType || selectedOffer.type)}>
                      {propertyTypes.find(t => t.value === (selectedOffer.propertyType || selectedOffer.type))?.label}
                    </span>
                  </div>
                  <h3>{selectedOffer.title || 'بدون عنوان'}</h3>
                  <div className="view-location">
                    <MapPin size={18} />
                    <span>{selectedOffer.city} - {selectedOffer.district}</span>
                  </div>
                  <div className="view-price">{formatPrice(selectedOffer.price, 'SAR', { onRequest: selectedOffer.priceOnRequest })}</div>

                  {/* ── 4. Unified specs grid (merged from lines 3274–3421) ──
                      All fields in one grid: basic specs + additional details.
                      offerNature kept visible as required. */}
                  <div className="view-specs-grid">
                    {selectedOffer.area > 0 && (
                      <div className="spec-card">
                        <Maximize size={20} />
                        <div className="spec-info">
                          <span className="spec-label">المساحة</span>
                          <span className="spec-value">{formatArea(selectedOffer.area)}</span>
                        </div>
                      </div>
                    )}
                    {selectedOffer.rooms > 0 && (
                      <div className="spec-card">
                        <Bed size={20} />
                        <div className="spec-info">
                          <span className="spec-label">الغرف</span>
                          <span className="spec-value">{selectedOffer.rooms}</span>
                        </div>
                      </div>
                    )}
                    {selectedOffer.bathrooms > 0 && (
                      <div className="spec-card">
                        <Bath size={20} />
                        <div className="spec-info">
                          <span className="spec-label">الحمامات</span>
                          <span className="spec-value">{selectedOffer.bathrooms}</span>
                        </div>
                      </div>
                    )}
                    {selectedOffer.lounges > 0 && (
                      <div className="spec-card">
                        <Home size={20} />
                        <div className="spec-info">
                          <span className="spec-label">الصالات</span>
                          <span className="spec-value">{selectedOffer.lounges}</span>
                        </div>
                      </div>
                    )}
                    {selectedOffer.kitchens > 0 && (
                      <div className="spec-card">
                        <Home size={20} />
                        <div className="spec-info">
                          <span className="spec-label">المطابخ</span>
                          <span className="spec-value">{selectedOffer.kitchens}</span>
                        </div>
                      </div>
                    )}
                    {selectedOffer.age > 0 && (
                      <div className="spec-card">
                        <Calendar size={20} />
                        <div className="spec-info">
                          <span className="spec-label">عمر العقار</span>
                          <span className="spec-value">{selectedOffer.age} سنة</span>
                        </div>
                      </div>
                    )}
                    {selectedOffer.floors > 0 && (
                      <div className="spec-card">
                        <Building2 size={20} />
                        <div className="spec-info">
                          <span className="spec-label">الأدوار</span>
                          <span className="spec-value">{selectedOffer.floors}</span>
                        </div>
                      </div>
                    )}
                    {selectedOffer.streetWidth > 0 && (
                      <div className="spec-card">
                        <MapPin size={20} />
                        <div className="spec-info">
                          <span className="spec-label">عرض الشارع</span>
                          <span className="spec-value">{selectedOffer.streetWidth} م</span>
                        </div>
                      </div>
                    )}
                    {selectedOffer.direction && (
                      <div className="spec-card">
                        <MapPin size={20} />
                        <div className="spec-info">
                          <span className="spec-label">الواجهة</span>
                          <span className="spec-value">
                            {selectedOffer.direction === 'north' ? 'شمال' :
                             selectedOffer.direction === 'south' ? 'جنوب' :
                             selectedOffer.direction === 'east' ? 'شرق' :
                             selectedOffer.direction === 'west' ? 'غرب' :
                             selectedOffer.direction === 'northeast' ? 'شمال شرقي' :
                             selectedOffer.direction === 'northwest' ? 'شمال غربي' :
                             selectedOffer.direction === 'southeast' ? 'جنوب شرقي' :
                             selectedOffer.direction === 'southwest' ? 'جنوب غربي' : selectedOffer.direction}
                          </span>
                        </div>
                      </div>
                    )}
                    {/* تاريخ الإضافة — merged from view-additional-details */}
                    {(selectedOffer.addedAt || selectedOffer.createdAt) && (
                      <div className="spec-card">
                        <Calendar size={20} />
                        <div className="spec-info">
                          <span className="spec-label">تاريخ الإضافة</span>
                          <span className="spec-value">{formatGregorianDate(selectedOffer.addedAt || selectedOffer.createdAt)}</span>
                        </div>
                      </div>
                    )}
                    {/* نوع الصك + صفة العرض (بيع) */}
                    {selectedOffer.offerType === 'sale' && (
                      <>
                        <div className="spec-card">
                          <FileText size={20} />
                          <div className="spec-info">
                            <span className="spec-label">نوع الصك</span>
                            <span className="spec-value">{selectedOffer.deedType === 'free' ? 'حر' : selectedOffer.deedType === 'mortgaged' ? 'مرهون' : '-'}</span>
                          </div>
                        </div>
                        <div className="spec-card">
                          <User size={20} />
                          <div className="spec-info">
                            <span className="spec-label">صفة العرض</span>
                            <span className="spec-value">
                              {selectedOffer.offerNature === 'owner' ? 'المالك' :
                               selectedOffer.offerNature === 'direct' ? 'مباشر' :
                               selectedOffer.offerNature === 'withDirect' ? 'مع المباشر' :
                               selectedOffer.offerNature === 'multiple' ? 'أكثر من وسيط' :
                               selectedOffer.offerNature === 'indirect' ? 'غير مباشر' : '-'}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                    {/* دفعات + كهرباء + ماء + صفة العرض (إيجار) */}
                    {selectedOffer.offerType === 'rent' && (
                      <>
                        <div className="spec-card">
                          <Home size={20} />
                          <div className="spec-info">
                            <span className="spec-label">يقبل دفعات</span>
                            <span className="spec-value">{selectedOffer.acceptsPayments === 'yes' ? 'نعم' : 'لا'}</span>
                          </div>
                        </div>
                        <div className="spec-card">
                          <Home size={20} />
                          <div className="spec-info">
                            <span className="spec-label">الكهرباء</span>
                            <span className="spec-value">{selectedOffer.electricity === 'independent' ? 'مستقل' : 'مشترك'}</span>
                          </div>
                        </div>
                        <div className="spec-card">
                          <Home size={20} />
                          <div className="spec-info">
                            <span className="spec-label">الماء</span>
                            <span className="spec-value">{selectedOffer.water === 'independent' ? 'مستقل' : 'مشترك'}</span>
                          </div>
                        </div>
                        <div className="spec-card">
                          <User size={20} />
                          <div className="spec-info">
                            <span className="spec-label">صفة العرض</span>
                            <span className="spec-value">
                              {selectedOffer.offerNature === 'owner' ? 'المالك' :
                               selectedOffer.offerNature === 'direct' ? 'مباشر' :
                               selectedOffer.offerNature === 'withDirect' ? 'مع المباشر' :
                               selectedOffer.offerNature === 'multiple' ? 'أكثر من وسيط' :
                               selectedOffer.offerNature === 'indirect' ? 'غير مباشر' : '-'}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* ── 5. Description + features (moved from lines 3715–3730) ── */}
                  {selectedOffer.description && (
                    <div className="view-description">
                      <h4>الوصف</h4>
                      <p>{selectedOffer.description}</p>
                    </div>
                  )}
                  {selectedOffer.features && selectedOffer.features.length > 0 && (
                    <div className="view-features">
                      <h4>المميزات</h4>
                      <div className="features-list">
                        {selectedOffer.features.map((feature, idx) => (
                          <span key={idx} className="feature-tag">{feature}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── 6. Owner info + private comment (unchanged position relative to each other) ── */}
                  {(selectedOffer.ownerName || selectedOffer.ownerPhone) && (
                    <div className="view-owner-info">
                      <h4>معلومات صاحب العرض</h4>
                      <div className="owner-details">
                        {selectedOffer.ownerName && (
                          <div className="owner-item">
                            <User size={18} />
                            <span>{selectedOffer.ownerName}</span>
                          </div>
                        )}
                        {selectedOffer.ownerPhone && (
                          <div className="owner-item">
                            <Phone size={18} />
                            <span dir="ltr">{selectedOffer.ownerPhone}</span>
                            <a href={`https://wa.me/966${selectedOffer.ownerPhone.replace(/^0/, '')}`} target="_blank" rel="noopener noreferrer" className="whatsapp-link">
                              <FaWhatsapp size={16} />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {selectedOffer.comment && (
                    <div className="view-comment">
                      <h4>ملاحظات خاصة (تظهر لك فقط)</h4>
                      <p>{selectedOffer.comment}</p>
                    </div>
                  )}

                  {/* ── 7. Private documents + external links (moved from lines 3656–3713) ── */}
                  <div className="view-images-gallery">
                    <h4>{t('offers.privateDocuments.title')}</h4>
                    <div className="text-muted" style={{ marginBottom: '8px', fontSize: '12px' }}>
                      {t('offers.privateDocuments.authorizedHint')}
                    </div>
                    {loadingPrivateImages ? (
                      <div className="empty-state" style={{ padding: '12px' }}>{t('offers.privateDocuments.loading')}</div>
                    ) : viewPrivateImages.length === 0 ? (
                      <div className="empty-state" style={{ padding: '12px' }}>{t('offers.privateDocuments.empty')}</div>
                    ) : (
                      <div className="images-grid">
                        {viewPrivateImages.map((doc, idx) => (
                          <div key={doc._id || idx} className="gallery-item" style={{ padding: '8px' }}>
                            <div className="file-chip" style={{ marginBottom: '8px' }}>{(doc.mimeType || '').includes('pdf') ? 'PDF' : 'IMG'}</div>
                            <div style={{ fontSize: '12px', wordBreak: 'break-word' }}>{doc.originalName || 'مستند خاص'}</div>
                            <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                              <button type="button" className="btn btn-outline btn-sm" onClick={() => handleOpenPrivateDoc(selectedOffer._id || selectedOffer.id, doc)}>
                                <Eye size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {selectedOffer.externalLinks && selectedOffer.externalLinks.length > 0 && (
                    <div className="view-external-links">
                      <h4>
                        <LinkIcon size={20} />
                        الروابط الخارجية ({selectedOffer.externalLinks.length})
                      </h4>
                      <div className="external-links-list">
                        {selectedOffer.externalLinks.map((link, idx) => (
                          <a
                            key={idx}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="external-link-item"
                          >
                            <div className="link-icon">
                              {link.type === 'video' && <span>🎥</span>}
                              {link.type === 'file' && <span>📄</span>}
                              {link.type === 'website' && <span>🌐</span>}
                              {link.type === 'other' && <span>🔗</span>}
                            </div>
                            <div className="link-details">
                              <span className="link-title">{link.title || link.url}</span>
                              {link.title && <span className="link-url">{link.url}</span>}
                            </div>
                            <div className="link-arrow">→</div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── 8. Matching requests + nested preview modal (moved from lines 3455–3626) ── */}
                  <div className="view-offer-matches-section" onClick={(e) => e.stopPropagation()}>
                    <div className="view-offer-matches-head">
                      <h4 className="view-offer-matches-title">الطلبات المطابقة</h4>
                      <button type="button" className="mrc-matches-toggle" onClick={handleToggleOfferMatches}>
                        {showOfferMatches ? 'إخفاء المطابقات' : `عرض المطابقات (${selectedOffer.matchesCount ?? 0})`}
                      </button>
                    </div>
                    {showOfferMatches && (
                      renderOfferMatchesList({
                        matches: offerMatches,
                        loading: offerMatchesLoading,
                        error: offerMatchesError,
                        onPreview: setPreviewRequest
                      })
                    )}
                  </div>

                  {/* Request Full Preview Modal (kept exactly as-is, nested inside matching section) */}
                  {previewRequest && (
                    <div className="modal-overlay" onClick={() => setPreviewRequest(null)}>
                      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                          <h2><Eye size={24} />تفاصيل الطلب</h2>
                          <button className="modal-close" onClick={() => setPreviewRequest(null)}>
                            <X size={24} />
                          </button>
                        </div>
                        <div className="modal-body">
                          <div className="view-request-content">
                            {(() => {
                              const req = previewRequest;
                              const missingFieldsCount = getMissingRequestFields(req).length;
                              const budgetValue = Number(req.budget) > 0
                                ? formatPrice(req.budget)
                                : (req.noBudgetPreference ? 'لا يهم' : 'غير محددة');
                              const areaValue = Number(req.area || req.areaMin) > 0
                                ? formatArea(req.area || req.areaMin)
                                : (req.noAreaPreference ? 'لا يهم' : 'غير محددة');
                              return (
                                <div className="request-preview-panel">
                                  <div className="view-request-header">
                                    <div className="client-avatar large">{req.clientName?.charAt(0) || 'ع'}</div>
                                    <div className="client-info-view">
                                      <h3>{req.clientName}</h3>
                                      <div className="client-phone-view">
                                        <Phone size={16} />
                                        <span dir="ltr">{req.clientPhone}</span>
                                        {req.clientPhone && (
                                          <a href={`https://wa.me/966${req.clientPhone?.replace(/^0/, '')}`} target="_blank" rel="noopener noreferrer" className="whatsapp-link" aria-label="واتساب">
                                            <FaWhatsapp size={16} />
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="request-preview-badges">
                                    <span className="request-preview-badge success">{getRequestStatusLabel(req.status)}</span>
                                    <span className="request-preview-badge warning">{missingFieldsCount} حقول ناقصة</span>
                                    {req.matchesCount > 0 && (
                                      <span className="request-preview-badge info">{req.matchesCount} عرض مطابق</span>
                                    )}
                                    {(req.addedAt || req.createdAt) && (
                                      <span className="request-preview-badge info">أضيف: {formatGregorianDate(req.addedAt || req.createdAt)}</span>
                                    )}
                                  </div>

                                  <section className="request-preview-section">
                                    <h4 className="request-preview-section-title">المواصفات</h4>
                                    <div className="request-preview-spec-tags">
                                      <span className="request-preview-spec-pill">نوع الطلب: {req.requestType === 'buy' ? 'شراء' : 'إيجار'}</span>
                                      <span className="request-preview-spec-pill">نوع العقار: {propertyTypes.find(pt => pt.value === req.propertyType)?.label || req.propertyType || 'غير محدد'}</span>
                                      <span className="request-preview-spec-pill">المدينة: {req.city || 'غير محدد'}</span>
                                      {(req.districts?.length > 0 || req.district) && (
                                        <span className="request-preview-spec-pill">الحي: {req.districts?.length > 0 ? req.districts.join('، ') : req.district}</span>
                                      )}
                                      <span className="request-preview-spec-pill">الميزانية: {budgetValue}</span>
                                      <span className="request-preview-spec-pill">المساحة: {areaValue}</span>
                                      {Number(req.age) > 0 && (
                                        <span className="request-preview-spec-pill">العمر: {req.age} سنة</span>
                                      )}
                                    </div>
                                  </section>

                                  <section className="request-preview-section">
                                    <h4 className="request-preview-section-title">تفاصيل التواصل</h4>
                                    <div className="request-preview-details-text">
                                      <p><strong>التواصل:</strong> <span dir="ltr">{req.clientPhone || 'غير متوفر'}</span></p>
                                      {req.clientSeriousness && (
                                        <p>
                                          <strong>جدية العميل:</strong>{' '}
                                          {req.clientSeriousness === 'very_interested' && 'مهتم جداً'}
                                          {req.clientSeriousness === 'interested' && 'مهتم'}
                                          {req.clientSeriousness === 'potential' && 'محتمل'}
                                        </p>
                                      )}
                                      {req.lastContact && (
                                        <p><strong>آخر تواصل:</strong> {formatGregorianDate(req.lastContact)}</p>
                                      )}
                                    </div>
                                  </section>

                                  {(req.requirements || req.notes) && (
                                    <section className="request-preview-section">
                                      <h4 className="request-preview-section-title">التفاصيل</h4>
                                      <div className="request-preview-details-text">
                                        {req.requirements ? (
                                          <p dangerouslySetInnerHTML={{
                                            __html: (req.requirements || '').replace(
                                              /(https?:\/\/[^\s]+)/g,
                                              '<a href="$1" target="_blank" rel="noopener noreferrer" class="inline-link">$1</a>'
                                            )
                                          }} />
                                        ) : null}
                                        {req.notes && <p className="request-preview-notes">{req.notes}</p>}
                                      </div>
                                    </section>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button className="btn btn-outline" onClick={() => setPreviewRequest(null)}>إغلاق</button>
                          {previewRequest.clientPhone && (
                            <button className="btn btn-success" onClick={() => handleShareOfferToRequest(previewRequest)}>
                              <FaWhatsapp size={16} />
                              مشاركة العرض
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>

            {/* ── 9. Modal footer ── */}
            <div className="modal-footer view-modal-footer">
              <button className="btn btn-primary" onClick={() => { cancelNewOfferMatchFlow(); setShowViewModal(false); setViewPrivateImages([]); handleOpenEdit(selectedOffer); }}>
                <Edit size={16} />
                تعديل
              </button>
              <button className="btn btn-secondary" onClick={() => handleShareOffer(selectedOffer)}>
                <Share2 size={16} />
                مشاركة
              </button>
              <button className="btn btn-image-share" disabled aria-disabled="true" title="قريبًا">
                <Camera size={16} />
                صورة <span className="coming-soon-badge">قريبًا</span>
              </button>
            </div>

            <OfferShareCard
              ref={shareCardRef}
              offer={selectedOffer}
              offerImage={getOfferImage(selectedOffer)}
              propertyTypes={propertyTypes}
              userName={user?.name || ''}
              formatPrice={formatPrice}
              formatArea={formatArea}
            />
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingOffer && (
        <div className="modal-overlay" onClick={handleCloseEditModal}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Edit size={24} />
                تعديل العرض
              </h2>
              <button className="modal-close" onClick={handleCloseEditModal}>
                <X size={24} />
              </button>
            </div>

            {/* مؤشر الخطوات للتعديل — قابل للنقر للتنقل بين الخطوات */}
            <div className="step-indicator" style={{ flexShrink: 0, background: 'var(--surface-color)' }}>
              {[
                { num: '١', label: 'العقار والموقع' },
                { num: '٢', label: 'التفاصيل' },
                { num: '٣', label: 'معلومات خاصة' },
                { num: '٤', label: 'الوسائط' },
              ].map((step, index) => {
                const stepNum = index + 1;
                return (
                  <div
                    key={index}
                    className={`step-item ${editStep === stepNum ? 'active' : ''} ${editStep > stepNum ? 'done' : ''} clickable`}
                    onClick={() => setEditStep(stepNum)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="step-dot">{step.num}</span>
                    <span className="step-label">{step.label}</span>
                  </div>
                );
              })}
            </div>

            <div className="modal-body">
              <div className="offer-form">

                {/* ===== EDIT STEP 1: العقار والموقع ===== */}
                {editStep === 1 && (
                <>
                <div className="form-row two-cols form-row-pair">
                  <div className="form-group">
                    <label>{t('offers.propertyType')} <span className="required">*</span></label>
                    <select
                      className="form-control"
                      value={editingOffer.propertyType || editingOffer.type}
                      onChange={(e) => setEditingOffer({ ...editingOffer, propertyType: e.target.value, type: e.target.value })}
                    >
                      {propertyTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('offers.offerType')} <span className="required">*</span></label>
                    <select
                      className="form-control"
                      value={editingOffer.offerType}
                      onChange={(e) => setEditingOffer({ ...editingOffer, offerType: e.target.value })}
                    >
                      <option value="sale">{t('offers.sale')}</option>
                      <option value="rent">{t('offers.rent')}</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('offers.title_field')} <span className="required">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={editingOffer.title || ''}
                      onChange={(e) => setEditingOffer({ ...editingOffer, title: e.target.value })}
                      placeholder="مثال: فيلا فاخرة للبيع حي الحجاز"
                      required
                    />
                  </div>
                </div>

                <div className="form-row two-cols">
                  <div className="form-group">
                    <label>{t('offers.city')} <span className="required">*</span></label>
                    <SearchableSelect
                      options={[
                        ...sortedCities.map(city => ({ value: city.label, label: city.label })),
                        ...(editingOffer.city && !sortedCities.some(c => c.label === editingOffer.city)
                          ? [{ value: editingOffer.city, label: editingOffer.city }]
                          : [])
                      ]}
                      value={editingOffer.city}
                      onChange={(value) => setEditingOffer({ ...editingOffer, city: value, region: '', district: '' })}
                      placeholder={t('common.selectCity')}
                      searchPlaceholder="ابحث عن المدينة..."
                      loading={loadingLocations}
                      disabled={loadingLocations}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('offers.district')} <span className="required">*</span></label>
                    <SearchableSelect
                      options={[
                        ...getAllDistrictsForCity(editingOffer.city).map(d => ({ value: d, label: d })),
                        ...(editingOffer.district && !getAllDistrictsForCity(editingOffer.city).includes(editingOffer.district)
                          ? [{ value: editingOffer.district, label: editingOffer.district }]
                          : [])
                      ]}
                      value={editingOffer.district}
                      onChange={(value) => setEditingOffer({ ...editingOffer, district: value })}
                      placeholder="اختر الحي"
                      searchPlaceholder="ابحث عن الحي..."
                      disabled={!editingOffer.city || loadingLocations}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('offers.status')}</label>
                    <select
                      className="form-control"
                      value={editingOffer.status}
                      onChange={(e) => setEditingOffer({ ...editingOffer, status: e.target.value })}
                    >
                      <option value="updated">محدث</option>
                      <option value="not_updated">غير محدث</option>
                    </select>
                  </div>
                </div>

                {/* خيار "عرض خاص" يظهر دائماً بغض النظر عن حالة الشبكة */}
                <div className="form-row">
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={!editingOffer.showInNetwork}
                        onChange={(e) => setEditingOffer({ ...editingOffer, showInNetwork: !e.target.checked })}
                      />
                      <span>{t('offers.showInNetwork')}</span>
                    </label>
                  </div>
                </div>
                </>
                )}

                {/* ===== EDIT STEP 2: التفاصيل ===== */}
                {editStep === 2 && (
                <>
                <div className="form-row two-cols">
                  <div className="form-group">
                    <div className="field-label-row">
                      <label className="form-label-text">{t('offers.price')} (ر.س) <span className="required">*</span></label>
                      <div className="field-label-actions">
                        <label className="later-checkbox">
                          <input
                            type="checkbox"
                            checked={Boolean(editingOffer.priceOnRequest)}
                            onChange={(e) => setEditingOffer({
                              ...editingOffer,
                              priceOnRequest: e.target.checked,
                              price: e.target.checked ? 0 : editingOffer.price
                            })}
                          />
                          <span>على السوم</span>
                        </label>
                      </div>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      value={formatNumberWithCommas(editingOffer.price)}
                      onChange={(e) => setEditingOffer({ ...editingOffer, price: parseFormattedNumber(e.target.value) })}
                      placeholder={editingOffer.priceOnRequest ? 'على السوم' : t('offers.price')}
                      disabled={Boolean(editingOffer.priceOnRequest)}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('offers.area')} (م²) {editingOffer.offerType === 'sale' && <span className="required">*</span>}</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editingOffer.area || ''}
                      onChange={(e) => setEditingOffer({ ...editingOffer, area: Number(e.target.value) })}
                    />
                  </div>
                </div>

                {/* Conditional fields based on offer type */}
                {editingOffer.offerType === 'sale' ? (
                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>{t('offers.deedType')}</label>
                      <select
                        className="form-control"
                        value={editingOffer.deedType || editingOffer.ownershipStatus || 'free'}
                        onChange={(e) => setEditingOffer({ ...editingOffer, deedType: e.target.value })}
                      >
                        <option value="free">{t('offers.deedFree')}</option>
                        <option value="mortgaged">{t('offers.deedMortgaged')}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('offers.offerNature')}</label>
                      <select
                        className="form-control"
                        value={editingOffer.offerNature || 'direct'}
                        onChange={(e) => setEditingOffer({ ...editingOffer, offerNature: e.target.value })}
                      >
                        <option value="direct">{t('offers.natureDirectWithOwner', 'مباشر')}</option>
                        <option value="withDirect">{t('offers.natureWithDirect', 'مع المباشر')}</option>
                        <option value="multiple">{t('offers.natureWithMe2OrMore', 'معي 2 او اكثر')}</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <>
                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>{t('offers.acceptsPayments')}</label>
                      <select
                        className="form-control"
                        value={editingOffer.acceptsPayments || 'no'}
                        onChange={(e) => setEditingOffer({ ...editingOffer, acceptsPayments: e.target.value })}
                      >
                        <option value="yes">{t('offers.acceptsPaymentsYes')}</option>
                        <option value="no">{t('offers.acceptsPaymentsNo')}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('offers.offerNature')}</label>
                      <select
                        className="form-control"
                        value={editingOffer.offerNature || 'direct'}
                        onChange={(e) => setEditingOffer({ ...editingOffer, offerNature: e.target.value })}
                      >
                        <option value="direct">{t('offers.natureDirectWithOwner', 'مباشر')}</option>
                        <option value="withDirect">{t('offers.natureWithDirect', 'مع المباشر')}</option>
                        <option value="multiple">{t('offers.natureWithMe2OrMore', 'معي 2 او اكثر')}</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>{t('offers.electricity')}</label>
                      <select
                        className="form-control"
                        value={editingOffer.electricity || 'independent'}
                        onChange={(e) => setEditingOffer({ ...editingOffer, electricity: e.target.value })}
                      >
                        <option value="independent">{t('offers.independent')}</option>
                        <option value="shared">{t('offers.shared')}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('offers.water')}</label>
                      <select
                        className="form-control"
                        value={editingOffer.water || 'independent'}
                        onChange={(e) => setEditingOffer({ ...editingOffer, water: e.target.value })}
                      >
                        <option value="independent">{t('offers.independent')}</option>
                        <option value="shared">{t('offers.shared')}</option>
                      </select>
                    </div>
                  </div>
                  </>
                )}

                {/* Land fields */}
                {(editingOffer.propertyType || editingOffer.type) === 'land' && (
                  <>
                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>{t('offers.landPurpose')}</label>
                      <select
                        className="form-control"
                        value={editingOffer.landPurpose || 'residential'}
                        onChange={(e) => setEditingOffer({ ...editingOffer, landPurpose: e.target.value })}
                      >
                        <option value="residential">{t('offers.purposeResidential')}</option>
                        <option value="agricultural">{t('offers.purposeAgricultural')}</option>
                        <option value="industrial">{t('offers.purposeIndustrial')}</option>
                        <option value="commercial">{t('offers.purposeCommercial')}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('offers.deedType')}</label>
                      <select
                        className="form-control"
                        value={editingOffer.deedType || 'free'}
                        onChange={(e) => setEditingOffer({ ...editingOffer, deedType: e.target.value })}
                      >
                        <option value="free">{t('offers.deedFree')}</option>
                        <option value="mortgaged">{t('offers.deedMortgaged')}</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row three-cols">
                    <div className="form-group">
                      <label>{t('offers.pricePerMeter')} (ر.س/م²)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formatNumberWithCommas(editingOffer.pricePerMeter)}
                        onChange={(e) => setEditingOffer({ ...editingOffer, pricePerMeter: parseFormattedNumber(e.target.value) })}
                        placeholder="500"
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('offers.street')} (م)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editingOffer.streetWidth || editingOffer.street || ''}
                        onChange={(e) => setEditingOffer({ ...editingOffer, streetWidth: Number(e.target.value), street: Number(e.target.value) })}
                        placeholder="20"
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('offers.direction')}</label>
                      <select
                        className="form-control"
                        value={editingOffer.direction || ''}
                        onChange={(e) => setEditingOffer({ ...editingOffer, direction: e.target.value })}
                      >
                        <option value="">{t('offers.selectDirection')}</option>
                        <option value="north">{t('offers.north')}</option>
                        <option value="south">{t('offers.south')}</option>
                        <option value="east">{t('offers.east')}</option>
                        <option value="west">{t('offers.west')}</option>
                        <option value="northeast">{t('offers.northeast')}</option>
                        <option value="northwest">{t('offers.northwest')}</option>
                        <option value="southeast">{t('offers.southeast')}</option>
                        <option value="southwest">{t('offers.southwest')}</option>
                      </select>
                    </div>
                  </div>
                  </>
                )}

                {/* غير أرض: غرف، حمامات، أدوار، اتجاه، شارع، عمر */}
                {(editingOffer.propertyType || editingOffer.type) !== 'land' && (
                  <>
                  <div className="form-row three-cols">
                    <div className="form-group">
                      <label>{t('offers.rooms')}</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editingOffer.rooms || ''}
                        onChange={(e) => setEditingOffer({ ...editingOffer, rooms: Number(e.target.value) })}
                        placeholder="5"
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('offers.bathrooms')}</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editingOffer.bathrooms || ''}
                        onChange={(e) => setEditingOffer({ ...editingOffer, bathrooms: Number(e.target.value) })}
                        placeholder="3"
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('offers.floors')}</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editingOffer.floors || ''}
                        onChange={(e) => setEditingOffer({ ...editingOffer, floors: Number(e.target.value) })}
                        placeholder="2"
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>{t('offers.direction')}</label>
                      <select
                        className="form-control"
                        value={editingOffer.direction || ''}
                        onChange={(e) => setEditingOffer({ ...editingOffer, direction: e.target.value })}
                      >
                        <option value="">{t('offers.selectDirection')}</option>
                        <option value="north">{t('offers.north')}</option>
                        <option value="south">{t('offers.south')}</option>
                        <option value="east">{t('offers.east')}</option>
                        <option value="west">{t('offers.west')}</option>
                        <option value="northeast">{t('offers.northeast')}</option>
                        <option value="northwest">{t('offers.northwest')}</option>
                        <option value="southeast">{t('offers.southeast')}</option>
                        <option value="southwest">{t('offers.southwest')}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('offers.street')} (م)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editingOffer.streetWidth || editingOffer.street || ''}
                        onChange={(e) => setEditingOffer({ ...editingOffer, streetWidth: Number(e.target.value), street: Number(e.target.value) })}
                        placeholder="20"
                        min="0"
                      />
                    </div>
                  </div>
                  </>
                )}

                <div className="form-row two-cols">
                  <div className="form-group">
                    <label>{t('offers.age')} (سنة)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editingOffer.age || ''}
                      onChange={(e) => setEditingOffer({ ...editingOffer, age: Number(e.target.value) })}
                      placeholder="مثال: 5"
                    />
                  </div>
                  <div className="form-group" />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('offers.description')}</label>
                    <textarea
                      className="form-control expandable-textarea"
                      rows={4}
                      value={editingOffer.description || ''}
                      onChange={(e) => setEditingOffer({ ...editingOffer, description: e.target.value })}
                      onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                      ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                    />
                  </div>
                </div>
                </>
                )}

                {/* ===== EDIT STEP 3: معلومات خاصة ===== */}
                {editStep === 3 && (
                <>
                <div className="form-section-title">{t('offers.privateFields')}</div>

                <div className="form-row two-cols">
                  <div className="form-group">
                    <label>{t('offers.ownerName')}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editingOffer.ownerName || ''}
                      onChange={(e) => handleOwnerNameChange(e.target.value, setEditingOffer)}
                      placeholder="اسم صاحب العرض"
                      list="previous-owners-list"
                      autoComplete="off"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('offers.ownerPhone')} <small>({t('offers.phoneHint')})</small></label>
                    <input
                      type="tel"
                      className="form-control"
                      value={editingOffer.ownerPhone || ''}
                      onChange={(e) => setEditingOffer({ ...editingOffer, ownerPhone: e.target.value })}
                      placeholder="+966 55 559 0799"
                      dir="ltr"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('offers.comment')}</label>
                    <textarea
                      className="form-control expandable-textarea"
                      rows={3}
                      value={editingOffer.comment || ''}
                      onChange={(e) => setEditingOffer({ ...editingOffer, comment: e.target.value })}
                      onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                      ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                      placeholder={t('offers.commentPlaceholder')}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('offers.privateDocuments.title')}</label>
                    <div className="text-muted" style={{ marginBottom: '8px', fontSize: '12px' }}>
                      {t('offers.privateDocuments.editHint')}
                    </div>

                    {editingOffer.privateImages && editingOffer.privateImages.length > 0 && (
                      <div className="uploaded-preview">
                        {editingOffer.privateImages.map((doc, idx) => (
                          <div key={`existing-private-${idx}`} className="preview-item">
                            <div className="file-chip">{(doc.mimeType || '').includes('pdf') ? 'PDF' : 'IMG'}</div>
                            <small style={{ display: 'block', padding: '4px 6px', fontSize: '11px' }}>{doc.originalName || 'مستند خاص'}</small>
                            <button
                              type="button"
                              title="حذف"
                              onClick={() => handleDeletePrivateDoc(editingOffer._id || editingOffer.id, doc._id || doc.id)}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {editPrivateFiles.length > 0 && (
                      <div className="uploaded-preview">
                        {editPrivateFiles.map((doc, idx) => (
                          <div key={`new-private-${idx}`} className="preview-item">
                            <div className="file-chip">{doc.type.includes('pdf') ? 'PDF' : 'IMG'}</div>
                            <small style={{ display: 'block', padding: '4px 6px', fontSize: '11px' }}>{doc.name}</small>
                            <button
                              type="button"
                              onClick={() => setEditPrivateFiles(prev => prev.filter((_, i) => i !== idx))}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <label className="upload-area" style={{ borderStyle: 'dashed' }}>
                      <Upload size={32} />
                      <p>{t('offers.privateDocuments.addAction')}</p>
                      <span>JPG, PNG, WEBP, PDF حتى 10MB</span>
                      <input type="file" accept="image/*,application/pdf" multiple onChange={handleEditPrivateFilesUpload} />
                    </label>
                  </div>
                </div>
                </>
                )}

                {/* ===== EDIT STEP 4: الوسائط ===== */}
                {editStep === 4 && (
                <>
                {/* Images Section */}
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('offers.images')}</label>
                    {/* Existing images */}
                    {editingOffer.images && editingOffer.images.length > 0 && (
                      <div className="uploaded-preview">
                        {editingOffer.images.map((img, idx) => (
                          <div key={idx} className="preview-item">
                            <img src={img} alt={`${idx + 1}`} />
                            <button type="button" onClick={() => {
                              setEditingOffer(prev => ({ 
                                ...prev, 
                                images: prev.images.filter((_, i) => i !== idx) 
                              }));
                            }}><X size={14} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* New uploaded images */}
                    {editUploadedImages.length > 0 && (
                      <div className="uploaded-preview">
                        {editUploadedImages.map((img, idx) => (
                          <div key={`new-${idx}`} className="preview-item">
                            <img src={img} alt={`New ${idx + 1}`} />
                            <span className="new-badge">جديد</span>
                            <button type="button" onClick={() => {
                              setEditUploadedImages(prev => prev.filter((_, i) => i !== idx));
                              setEditImageFiles(prev => prev.filter((_, i) => i !== idx));
                            }}><X size={14} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <label className="upload-area">
                      <Upload size={32} />
                      <p>إضافة صور جديدة</p>
                      <span>PNG, JPG حتى 5MB</span>
                      <input type="file" accept="image/*" multiple onChange={handleEditImageUpload} />
                    </label>
                  </div>
                </div>

                {/* Videos Section */}
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('offers.videos') || 'الفيديوهات'}</label>
                    {/* Existing videos */}
                    {editingOffer.videos && editingOffer.videos.length > 0 && (
                      <div className="uploaded-preview">
                        {editingOffer.videos.map((video, idx) => (
                          <div key={idx} className="preview-item video-preview">
                            <video src={video} />
                            <span className="video-badge">فيديو {idx + 1}</span>
                            <button type="button" onClick={() => {
                              setEditingOffer(prev => ({ 
                                ...prev, 
                                videos: prev.videos.filter((_, i) => i !== idx) 
                              }));
                            }}><X size={14} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* New videos stay separate until save so deleting a preview also deletes its File. */}
                    {editUploadedVideos.length > 0 && (
                      <div className="uploaded-preview">
                        {editUploadedVideos.map((video, idx) => (
                          <div key={`new-video-${idx}`} className="preview-item video-preview">
                            <video src={video} />
                            <span className="new-badge">جديد</span>
                            <button type="button" onClick={() => {
                              setEditUploadedVideos(prev => prev.filter((_, i) => i !== idx));
                              setEditVideoFiles(prev => prev.filter((_, i) => i !== idx));
                            }}><X size={14} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <label className="upload-area">
                      <Upload size={32} />
                      <p>إضافة فيديوهات جديدة</p>
                      <span>MP4, WebM حتى 50MB</span>
                      <input type="file" accept="video/*" multiple onChange={handleEditVideoUpload} />
                    </label>
                  </div>
                </div>

                {/* تعديل الوسائط يقتصر على الصور والفيديوهات، مع إبقاء بيانات الروابط القديمة دون حذف. */}

                </>
                )}

              </div>
            </div>

            {/* أزرار التنقل بين خطوات التعديل */}
            <div className="modal-footer step-footer">
              <div className="step-nav-buttons">
                {/* السابق / إلغاء */}
                {editStep === 1 ? (
                  <button className="btn btn-outline step-btn-prev" onClick={handleCloseEditModal}>
                    إلغاء
                  </button>
                ) : (
                  <button
                    className="btn btn-outline step-btn-prev"
                    onClick={() => setEditStep(s => s - 1)}
                    disabled={saving}
                  >
                    السابق
                  </button>
                )}

                {/* التالي */}
                {editStep < 4 && (
                  <button
                    className="btn btn-secondary step-btn-next"
                    onClick={() => {
                      if (editStep === 1) {
                        if (!(editingOffer.propertyType || editingOffer.type)) { toast.warning('يرجى اختيار نوع العقار'); return; }
                        if (!editingOffer.offerType) { toast.warning('يرجى اختيار نوع العرض'); return; }
                        if (!editingOffer.city) { toast.warning('يرجى اختيار المدينة'); return; }
                        if (!editingOffer.district) { toast.warning('يرجى اختيار الحي'); return; }
                      }
                      if (editStep === 2) {
                        if (!editingOffer.priceOnRequest && !editingOffer.price) { toast.warning('يرجى إدخال السعر'); return; }
                        if (!editingOffer.area || Number(editingOffer.area) <= 0) { toast.warning('يرجى إدخال المساحة'); return; }
                      }
                      setEditStep(s => s + 1);
                    }}
                    disabled={saving}
                  >
                    التالي
                  </button>
                )}

                {/* حفظ — يظهر دائماً */}
                <button
                  className="btn btn-primary step-btn-save"
                  onClick={handleSaveEdit}
                  disabled={saving}
                >
                  {saving ? <><Loader size={16} className="spinner" /> جاري الحفظ...</> : 'حفظ التغييرات'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Network Visibility Modal */}
      {showNetworkModal && networkOffer && (
        <div className="modal-overlay" onClick={() => { setShowNetworkModal(false); setNetworkOffer(null); }}>
          <div className="modal-content network-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Network size={24} />
                إعدادات الظهور في الشبكة
              </h2>
              <button className="modal-close" onClick={() => { setShowNetworkModal(false); setNetworkOffer(null); }}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="network-offer-preview">
                {getOfferImage(networkOffer) ? <img src={getOfferImage(networkOffer)} alt={networkOffer.title} /> : <div className="no-image-placeholder" style={{width: '120px', height: '80px', borderRadius: '8px'}}>لا توجد صور</div>}
                <div className="preview-info">
                  <h4>{networkOffer.title || 'بدون عنوان'}</h4>
                  <p>{networkOffer.city} - {networkOffer.district}</p>
                  <span className="price">{formatPrice(networkOffer.price, 'SAR', { onRequest: networkOffer.priceOnRequest })}</span>
                </div>
              </div>

              <div className="network-fields-section">
                <div className="network-fields-header">
                  <h4>إخفاء حقول اختيارية من المشاركة:</h4>
                </div>
                <p className="hint">كل البيانات العامة تظهر تلقائياً، ويمكنك فقط إخفاء: حالة العرض، صفة العرض. الحقول الخاصة مخفية دائماً.</p>
                
                <div className="network-fields-grid">
                  {availableNetworkFields.map((field) => (
                    <label 
                      key={field.key} 
                      className={`network-field-item ${field.required ? 'required' : ''} ${networkVisibleFields.includes(field.key) ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={networkVisibleFields.includes(field.key) || field.required}
                        disabled={field.required}
                        onChange={() => handleToggleNetworkField(field.key)}
                      />
                      <span className="field-label">
                        {field.label}
                        {field.required && <span className="required-star">*</span>}
                      </span>
                      {networkOffer[field.key] !== undefined && networkOffer[field.key] !== '' && networkOffer[field.key] !== null && (
                        <span className="field-value">
                          {field.key === 'price' ? formatPrice(networkOffer[field.key], 'SAR', { onRequest: networkOffer.priceOnRequest }) :
                           field.key === 'area' ? `${networkOffer[field.key]} م²` :
                           field.key === 'images' ? `${(networkOffer.images?.length || 0)} صور` :
                           field.key === 'features' ? `${(networkOffer.features?.length || 0)} مميزات` :
                           typeof networkOffer[field.key] === 'boolean' ? (networkOffer[field.key] ? 'نعم' : 'لا') :
                           String(networkOffer[field.key]).substring(0, 20)}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              <div className="network-summary">
                <p>
                  <strong>الحقول الاختيارية الظاهرة:</strong> {networkVisibleFields.length} من {availableNetworkFields.length}
                </p>
              </div>
            </div>
            <div className="modal-footer network-footer">
              {networkOffer.showInNetwork && (
                <button className="btn btn-danger" onClick={handleRemoveFromNetwork}>
                  <X size={18} />
                  إزالة من الشبكة
                </button>
              )}
              <div className="footer-actions">
                <button className="btn btn-outline" onClick={() => { setShowNetworkModal(false); setNetworkOffer(null); }}>
                  إلغاء
                </button>
                <button className="btn btn-primary" onClick={handleSaveNetworkSettings}>
                  <Check size={18} />
                  {networkOffer.showInNetwork ? 'حفظ التغييرات' : 'إضافة للشبكة'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Matching Results Popup after adding offer */}
      {showMatchingPopup && (
        <div className="modal-overlay" onClick={() => { setShowMatchingPopup(false); setMatchingPopupResults([]); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Sparkles size={24} />
                طلبات مطابقة لهذا العرض
              </h2>
              <button className="modal-close" onClick={() => { setShowMatchingPopup(false); setMatchingPopupResults([]); }}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              {renderOfferMatchesList({
                matches: matchingPopupResults,
                loading: matchingPopupLoading,
                error: '',
                // Close only the match popup before opening the existing request preview.
                onPreview: (request) => {
                  setShowMatchingPopup(false);
                  setPreviewRequest(request);
                }
              })}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setShowMatchingPopup(false); setMatchingPopupResults([]); }}>
                إغلاق
              </button>
              {matchingPopupResults.length > 0 && (
                <button className="btn btn-primary" onClick={() => {
                  setShowMatchingPopup(false);
                  setMatchingPopupResults([]);
                  navigate('/requests?tab=matching');
                }}>
                  <Sparkles size={18} />
                  عرض كل المطابقات
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {fullscreenMedia && (
        <div className="media-fullscreen-overlay" onClick={() => setFullscreenMedia(null)}>
          <button type="button" className="media-fullscreen-close" onClick={() => setFullscreenMedia(null)} aria-label="إغلاق">
            <X size={26} />
          </button>
          <div className="media-fullscreen-content" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const items = fullscreenMedia.items || [fullscreenMedia];
              const index = Math.min(fullscreenMedia.index || 0, items.length - 1);
              const active = items[index];
              return (
                <>
                  {active.type === 'video' ? (
                    <video src={active.src} controls autoPlay />
                  ) : (
                    <img src={active.src} alt={active.label || 'صورة العرض'} />
                  )}
                  {items.length > 1 && (
                    <>
                      <button type="button" className="media-fullscreen-nav media-fullscreen-prev" onClick={() => setFullscreenMedia(prev => ({ ...prev, index: Math.max((prev.index || 0) - 1, 0) }))} disabled={index === 0} aria-label="السابق">&#8250;</button>
                      <button type="button" className="media-fullscreen-nav media-fullscreen-next" onClick={() => setFullscreenMedia(prev => ({ ...prev, index: Math.min((prev.index || 0) + 1, items.length - 1) }))} disabled={index === items.length - 1} aria-label="التالي">&#8249;</button>
                      <span className="media-fullscreen-counter">{index + 1} / {items.length}</span>
                    </>
                  )}
                  <button type="button" className="media-fullscreen-toggle" onClick={() => setFullscreenMedia(null)} aria-label="الخروج من ملء الشاشة">
                    <Maximize size={20} />
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
    </PermissionGate>
  );
};

export default OffersPage;
